import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { Key } from 'react';
import { useLocation as useRouterLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Breadcrumb,
  Button,
  Checkbox,
  Dropdown,
  Space,
  Input,
  List,
  Row,
  Col,
  Typography,
  Form,
  message,
  Tag,
  Tooltip,
  Spin,
  Segmented,
  Select,
  Switch,
  Modal as AntModal,
  Divider,
  theme,
} from 'antd';
import type { MenuProps, TreeProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined, FolderOutlined, ReloadOutlined, MoreOutlined, PictureOutlined, ApartmentOutlined, HomeOutlined, CopyOutlined, SettingOutlined } from '@ant-design/icons';
import * as Icons from '@ant-design/icons';
import type { DataNode } from 'antd/es/tree';
import { FactoryLayoutViewResponsive } from '../components/FactoryLayoutViewResponsive';
import { LocationCategoryLegend } from '../components/LocationCategoryLegend';
import BranchLocationMapSettings from '../components/branch/BranchLocationMapSettings';
import LocationMapImageSettings from '../components/location/LocationMapImageSettings';
import LocationThumbnailSettings from '../components/location/LocationThumbnailSettings';
import { GeoLocationMapView } from '../components/location/GeoLocationMapView';
import { BuildingFloorNavigator } from '../components/location/BuildingFloorNavigator';
import EquipmentFormModal from '../components/EquipmentFormModal';
import { EquipmentDetailModal } from '../components/equipment/EquipmentDetailModal';
import { PartFormModal } from '../components/parts/PartFormModal';
import { PartDetailModal } from '../components/parts/PartDetailModal';
import { DocumentDetailModal } from '../components/document/DocumentDetailModal';
import { LocationsHeader } from './locations/components/LocationsHeader';
import { LocationsEmptyStateCard } from './locations/components/LocationsEmptyStateCard';
import { LocationsTreePanel } from './locations/components/LocationsTreePanel';
import { LocationDocumentsSection } from './locations/components/LocationDocumentsSection';
import { trpc } from '../lib/api';
import { withDevModalFilePath, NumericInput, RichTextEditor, MarkdownDisplay } from '../components/common';
import { UserDisplay } from '../components/common/UserDisplay';
import { useAuthStore } from '../stores/authStore';
import dayjs from 'dayjs';
import { logger } from '../utils/logger';
import { geoDebugLog } from '../utils/geoDebug';
import { featureFlags } from '../utils/featureFlags';
import { getMutationPendingState } from '../utils/trpcMutation';
import { usePermission } from '../hooks/usePermission';
import { PermissionButton } from '../components/common/PermissionButton';
import { useUsageMonitor } from '../hooks/useUsageMonitor';
import { computeVirtualLatLon, parseLatLon } from '../components/location/geoMapUtils';
import type {
  GeocodeCandidate,
  Location,
  LocationCategory,
  EquipmentRow,
  EquipmentApiResponse,
  PartRow,
  LocationsHashState,
} from './locations/types';
import {
  getAllLocations,
  getLocationMapModeLabel,
  getLocationMapModeTagColor,
  parseLocationsHash,
  buildLocationsHash,
} from './locations/utils';

const { Modal: DevModal } = withDevModalFilePath('client/src/pages/LocationsPageWithAPI.tsx');

const { Text } = Typography;

type AntIconComponent = React.ComponentType<React.ComponentProps<typeof EnvironmentOutlined>>;
type TreeDropInfo = Parameters<NonNullable<TreeProps['onDrop']>>[0];

const isEquipmentApiResponse = (value: unknown): value is EquipmentApiResponse => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as EquipmentApiResponse;
  return typeof candidate.id === 'string'
    && typeof candidate.name === 'string'
    && typeof candidate.isActive === 'boolean';
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeLocationUserSummary = (value: unknown): Location['createdByUser'] => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    name: typeof value.name === 'string' ? value.name : null,
    email: typeof value.email === 'string' ? value.email : null,
    avatarUrl: typeof value.avatarUrl === 'string' ? value.avatarUrl : null,
    preferences: isRecord(value.preferences) ? value.preferences : null,
  };
};

const normalizeLocationMapMode = (value: unknown): Location['mapMode'] => {
  if (value === 'image' || value === 'building' || value === 'folder' || value === 'geo') {
    return value;
  }
  return null;
};

const normalizeLocationNode = (value: unknown, fallbackLevel = 0): Location | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null;
  }

  const level = typeof value.level === 'number' ? value.level : fallbackLevel;
  const children = (Array.isArray(value.children) ? value.children : [])
    .map((child) => normalizeLocationNode(child, level + 1))
    .filter((child): child is Location => child !== null);

  return {
    id: value.id,
    name: value.name,
    displayId: typeof value.displayId === 'string' ? value.displayId : value.id,
    parentId: typeof value.parentId === 'string' ? value.parentId : null,
    categoryId: typeof value.categoryId === 'string' ? value.categoryId : null,
    thumbnailFileId: typeof value.thumbnailFileId === 'string' ? value.thumbnailFileId : null,
    mapMode: normalizeLocationMapMode(value.mapMode),
    mapBackgroundColor: typeof value.mapBackgroundColor === 'string' ? value.mapBackgroundColor : null,
    displayOrder: typeof value.displayOrder === 'number' ? value.displayOrder : null,
    isPositionLocked: typeof value.isPositionLocked === 'boolean' ? value.isPositionLocked : null,
    level,
    address: typeof value.address === 'string' ? value.address : null,
    latitude: typeof value.latitude === 'string' ? value.latitude : null,
    longitude: typeof value.longitude === 'string' ? value.longitude : null,
    pinLatitude: typeof value.pinLatitude === 'string' ? value.pinLatitude : null,
    pinLongitude: typeof value.pinLongitude === 'string' ? value.pinLongitude : null,
    description: typeof value.description === 'string' ? value.description : null,
    mapX: typeof value.mapX === 'number' ? value.mapX : null,
    mapY: typeof value.mapY === 'number' ? value.mapY : null,
    icon: typeof value.icon === 'string' ? value.icon : null,
    equipmentCount: typeof value.equipmentCount === 'number' ? value.equipmentCount : 0,
    partCount: typeof value.partCount === 'number' ? value.partCount : 0,
    children,
    createdByUser: normalizeLocationUserSummary(value.createdByUser),
    updatedByUser: normalizeLocationUserSummary(value.updatedByUser),
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : null,
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : null,
  };
};

const normalizeLocationTree = (value: unknown): Location[] => {
  const nodes =
    Array.isArray(value)
      ? value
      : isRecord(value) && Array.isArray(value.items)
        ? value.items
        : isRecord(value) && Array.isArray(value.data)
          ? value.data
          : [];

  return nodes
    .map((node) => normalizeLocationNode(node))
    .filter((node): node is Location => node !== null);
};

const normalizeLocationCategory = (value: unknown): LocationCategory | null => {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.companyId !== 'string' ||
    typeof value.categoryLabel !== 'string'
  ) {
    return null;
  }

  return {
    id: value.id,
    companyId: value.companyId,
    categoryLabel: value.categoryLabel,
    description: typeof value.description === 'string' ? value.description : null,
    displayOrder: typeof value.displayOrder === 'number' ? value.displayOrder : 0,
    color: typeof value.color === 'string' ? value.color : null,
    icon: typeof value.icon === 'string' ? value.icon : null,
    isBuilding: typeof value.isBuilding === 'boolean' ? value.isBuilding : false,
    isActive: typeof value.isActive === 'boolean' ? value.isActive : true,
    createdAt: value.createdAt instanceof Date ? value.createdAt : new Date(0),
    updatedAt: value.updatedAt instanceof Date ? value.updatedAt : new Date(0),
  };
};

const normalizeLocationCategoryList = (value: unknown): LocationCategory[] => {
  const categories =
    Array.isArray(value)
      ? value
      : isRecord(value) && Array.isArray(value.items)
        ? value.items
        : isRecord(value) && Array.isArray(value.data)
          ? value.data
          : [];

  return categories
    .map((category) => normalizeLocationCategory(category))
    .filter((category): category is LocationCategory => category !== null);
};

type BranchOption = {
  id: string;
  name: string;
  level?: number;
};

const normalizeBranchList = (value: unknown): BranchOption[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((branch) => {
    if (!isRecord(branch) || typeof branch.id !== 'string') {
      return [];
    }

    return [{
      id: branch.id,
      name: typeof branch.name === 'string' ? branch.name : branch.id,
      level: typeof branch.level === 'number' ? branch.level : undefined,
    }];
  });
};

const normalizePartRowCategory = (value: unknown): PartRow['category'] => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.categoryLabel !== 'string') {
    return null;
  }

  return {
    id: value.id,
    categoryLabel: value.categoryLabel,
    color: typeof value.color === 'string' ? value.color : null,
    icon: typeof value.icon === 'string' ? value.icon : null,
  };
};

export function LocationsPageWithAPI() {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { token } = theme.useToken();
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [pendingCreateCoordinates, setPendingCreateCoordinates] = useState<{ mapX: number; mapY: number } | null>(null);
  const [isMapImageModalVisible, setIsMapImageModalVisible] = useState(false);
  const [isThumbnailModalVisible, setIsThumbnailModalVisible] = useState(false);
  const [thumbnailTargetLocationId, setThumbnailTargetLocationId] = useState<string | null>(null);
  const [isCopyHierarchyModalVisible, setIsCopyHierarchyModalVisible] = useState(false);
  const [copyHierarchyName, setCopyHierarchyName] = useState('');
  const [copyHierarchyIncludeEquipments, setCopyHierarchyIncludeEquipments] = useState(true);
  const [parentLocationName, setParentLocationName] = useState<string | null>(null);
  const [isEquipmentModalVisible, setIsEquipmentModalVisible] = useState(false);
  const [isPartModalVisible, setIsPartModalVisible] = useState(false);
  const [equipmentCreateCategoryId, setEquipmentCreateCategoryId] = useState<string | null>(null);
  const [partCreateCategoryId, setPartCreateCategoryId] = useState<string | null>(null);
  const [viewingEquipmentId, setViewingEquipmentId] = useState<string | null>(null);
  const [viewingPartId, setViewingPartId] = useState<string | null>(null);
  const [viewingDocumentId, setViewingDocumentId] = useState<string | null>(null);
  const [showDetailInfo, setShowDetailInfo] = useState(false);
  const [isGeoClickPlaceMode, setIsGeoClickPlaceMode] = useState(false);
  const [isHierarchyMoveEnabled, setIsHierarchyMoveEnabled] = useState(false);
  const [geocodeCandidates, setGeocodeCandidates] = useState<GeocodeCandidate[]>([]);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const utils = trpc.useUtils();

  const modalMapMode = Form.useWatch('mapMode', form) as Location['mapMode'] | undefined;
  const modalAddress = Form.useWatch('address', form) as string | undefined;
  const modalParentId = Form.useWatch('parentId', form) as string | undefined;

  const geocodeAddressQuery = trpc.location.geocodeAddress.useQuery(
    { query: (modalAddress ?? '').trim() },
    { enabled: false, retry: 0 }
  );

  const applyGeocodeCandidate = useCallback((candidate: GeocodeCandidate) => {
    const currentPinLat = form.getFieldValue('pinLatitude') as string | null | undefined;
    const currentPinLon = form.getFieldValue('pinLongitude') as string | null | undefined;
    const pinLatEmpty = typeof currentPinLat !== 'string' || currentPinLat.trim().length === 0;
    const pinLonEmpty = typeof currentPinLon !== 'string' || currentPinLon.trim().length === 0;
    const shouldSetPin = pinLatEmpty || pinLonEmpty;

    form.setFieldsValue({
      address: candidate.displayName,
      latitude: candidate.latitude,
      longitude: candidate.longitude,
      ...(shouldSetPin ? { pinLatitude: candidate.latitude, pinLongitude: candidate.longitude } : {}),
    });
  }, [form]);

  const handleGeocodeSearch = useCallback(async () => {
    const query = (form.getFieldValue('address') as string | undefined)?.trim();
    if (!query) {
      messageApi.warning('住所を入力してください');
      return;
    }

    setGeocodeCandidates([]);

    const result = await geocodeAddressQuery.refetch();
    if (result.error) {
      const errorMessage = result.error instanceof Error ? result.error.message : '住所検索に失敗しました';
      messageApi.error(errorMessage);
      return;
    }

    const items = result.data?.items ?? [];
    if (items.length === 0) {
      messageApi.info('候補が見つかりませんでした');
      return;
    }

    setGeocodeCandidates(items);

    if (items.length === 1) {
      applyGeocodeCandidate(items[0]);
      messageApi.success('住所を設定しました');
      return;
    }

    messageApi.info('候補から選択してください');
  }, [applyGeocodeCandidate, form, geocodeAddressQuery, messageApi]);

  // ユーザー情報取得
  const { user } = useAuthStore();
  const companyId = user?.companyId;
  const { canManage, getDisabledMessage } = usePermission();

  // 設備上限チェック
  const { usage } = useUsageMonitor();
  const isEquipmentAtCapacity = usage?.equipmentLimit !== null &&
    usage?.equipmentLimit !== undefined &&
    usage?.equipmentCount !== undefined &&
    usage.equipmentCount >= usage.equipmentLimit;

  // ブランチ一覧を取得
  const { data: branches } = trpc.branch.list.useQuery(
    { companyId: companyId! },
    { enabled: !!companyId }
  );
  const branchOptions = useMemo(
    () => normalizeBranchList(branches),
    [branches]
  );

  const [hashState, setHashState] = useState<LocationsHashState>(() => parseLocationsHash(routerLocation.hash));
  const isApplyingHashRef = useRef(false);
  useEffect(() => {
    setHashState(parseLocationsHash(routerLocation.hash));
    isApplyingHashRef.current = true;
  }, [routerLocation.hash]);

  // 選択中のブランチID（デフォルトは最初のブランチ）
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [mapScopeLocationId, setMapScopeLocationId] = useState<string | null>(null);
  const [mapLocationsScopeMode, setMapLocationsScopeMode] = useState<'direct' | 'all'>('direct');
  const MAP_PANEL_SPAN_STORAGE_KEY = 'locations.mapPanelSpan';
  const MAP_PANEL_SPAN_OPTIONS = [14, 16, 18] as const;
  const [mapPanelSpan, setMapPanelSpan] = useState<number>(() => {
    if (typeof window === 'undefined') return 16;
    const raw = window.localStorage.getItem(MAP_PANEL_SPAN_STORAGE_KEY);
    const parsed = raw ? Number(raw) : 16;
    // 旧設定（最大=20）からの移行：一番近い「広(18)」へ寄せる
    if (parsed === 20) {
      return 18;
    }
    if (MAP_PANEL_SPAN_OPTIONS.includes(parsed as (typeof MAP_PANEL_SPAN_OPTIONS)[number])) {
      return parsed;
    }
    return 16;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(MAP_PANEL_SPAN_STORAGE_KEY, String(mapPanelSpan));
  }, [mapPanelSpan]);
  useEffect(() => {
    geoDebugLog('[LocationsPageWithAPI]', 'scope state changed', {
      mapScopeLocationId,
      mapLocationsScopeMode,
    });
  }, [mapLocationsScopeMode, mapScopeLocationId]);

  // ブランチが読み込まれたら、ハッシュ指定（あれば）を優先し、なければ最初のブランチを選択
  useEffect(() => {
    if (!branchOptions.length) {
      return;
    }

    const desiredBranchId = hashState.branchId;
    if (
      desiredBranchId
      && desiredBranchId !== selectedBranchId
      && branchOptions.some((b) => b.id === desiredBranchId)
    ) {
      setSelectedBranchId(desiredBranchId);
      return;
    }

    if (!selectedBranchId) {
      setSelectedBranchId(branchOptions[0].id);
    }
  }, [branchOptions, hashState.branchId, selectedBranchId]);

  // ブランチが変わったらスコープ/選択をリセット
  useEffect(() => {
    setMapScopeLocationId(null);
    setSelectedLocation(null);
  }, [selectedBranchId]);

  // 選択中のブランチのエリアマップ画像を取得
  const {
    data: locationMapImage,
    refetch: refetchLocationMapImage,
  } = trpc.branch.getLocationMapImage.useQuery(
    { branchId: selectedBranchId! },
    { enabled: !!selectedBranchId }
  );

  const {
    data: scopedLocationMapImage,
    refetch: refetchScopedLocationMapImage,
  } = trpc.location.getMapImage.useQuery(
    { id: mapScopeLocationId! },
    { enabled: !!mapScopeLocationId }
  );

  const defaultLocationMapImageSrc = '/images/factory/default-location-map.png';
  const locationMapImageDataResolved = locationMapImage !== undefined;
  const hasCustomLocationMapImage = Boolean(locationMapImage?.imageUrl);
  const shouldShowDefaultLocationMapImage = locationMapImageDataResolved && !hasCustomLocationMapImage;
  const locationMapBackgroundImageUrl = hasCustomLocationMapImage
    ? locationMapImage?.imageUrl ?? null
    : shouldShowDefaultLocationMapImage
      ? defaultLocationMapImageSrc
      : null;

  const scopedLocationMapBackgroundImageUrl =
    mapScopeLocationId ? (scopedLocationMapImage?.imageUrl ?? null) : null;

  const currentMapBackgroundImageUrl = mapScopeLocationId
    ? scopedLocationMapBackgroundImageUrl
    : locationMapBackgroundImageUrl;


  // 選択中のブランチを取得
  const selectedBranch = branchOptions.find((branch) => branch.id === selectedBranchId);

  // API呼び出し - selectedBranchIdでフィルタリング
  const locationsQueryInput = useMemo(
    () => ({ branchId: selectedBranchId || undefined }),
    [selectedBranchId]
  );

  const { data: locationsTreeData, isLoading, refetch, error } = trpc.location.getTreeWithCounts.useQuery(
    locationsQueryInput,
    { enabled: !!selectedBranchId }
  );
  const locationsTree = useMemo(
    () => normalizeLocationTree(locationsTreeData),
    [locationsTreeData]
  );
  const createMutation = trpc.location.create.useMutation();
  const updateMutation = trpc.location.update.useMutation();
  const moveInTreeMutation = trpc.location.moveInTree.useMutation({
    onMutate: async (input: {
      locationId: string;
      destinationParentId: string;
      destinationIndex: number;
      expectedUpdatedAt?: string | null;
    }) => {
      await utils.location.getTreeWithCounts.cancel(locationsQueryInput);
      const previous = utils.location.getTreeWithCounts.getData(locationsQueryInput) as Location[] | undefined;

      const cloneTree = (nodes: Location[]): Location[] => nodes.map((node) => ({
        ...node,
        children: node.children ? cloneTree(node.children) : [],
      }));

      const findNodeRef = (nodes: Location[], id: string): Location | null => {
        for (const node of nodes) {
          if (node.id === id) return node;
          if (node.children && node.children.length > 0) {
            const found = findNodeRef(node.children, id);
            if (found) return found;
          }
        }
        return null;
      };

      const findParentAndIndex = (
        nodes: Location[],
        id: string,
        parent: Location | null = null
      ): { parent: Location | null; index: number; siblings: Location[] } | null => {
        const idx = nodes.findIndex((n) => n.id === id);
        if (idx >= 0) {
          return { parent, index: idx, siblings: nodes };
        }
        for (const node of nodes) {
          const children = node.children ?? [];
          if (children.length === 0) continue;
          const found = findParentAndIndex(children, id, node);
          if (found) return found;
        }
        return null;
      };

      const applyLevelDelta = (node: Location, delta: number) => {
        node.level = (node.level ?? 0) + delta;
        const children = node.children ?? [];
        for (const child of children) {
          applyLevelDelta(child, delta);
        }
      };

      const renumberDisplayOrders = (siblings: Location[]) => {
        for (let i = 0; i < siblings.length; i++) {
          siblings[i].displayOrder = i * 10;
        }
      };

      utils.location.getTreeWithCounts.setData(locationsQueryInput, (current) => {
        if (!current) return current;
        const next = cloneTree(current as unknown as Location[]);

        const sourceRef = findNodeRef(next, input.locationId);
        if (!sourceRef) return current;

        const sourceParentInfo = findParentAndIndex(next, input.locationId);
        if (!sourceParentInfo) return current;

        const destinationParentRef = findNodeRef(next, input.destinationParentId);
        if (!destinationParentRef) return current;

        // Detach
        sourceParentInfo.siblings.splice(sourceParentInfo.index, 1);

        // Insert
        destinationParentRef.children = destinationParentRef.children ?? [];
        const destinationSiblings = destinationParentRef.children;
        const filteredDestinationSiblings = destinationSiblings.filter((n) => n.id !== input.locationId);
        const clampedIndex = Math.max(0, Math.min(input.destinationIndex, filteredDestinationSiblings.length));
        filteredDestinationSiblings.splice(clampedIndex, 0, sourceRef);
        destinationParentRef.children = filteredDestinationSiblings;

        // Update parentId + level
        const oldLevel = sourceRef.level ?? 0;
        const newLevel = (destinationParentRef.level ?? 0) + 1;
        sourceRef.parentId = destinationParentRef.id;
        applyLevelDelta(sourceRef, newLevel - oldLevel);

        // Re-number displayOrder for affected sibling arrays
        renumberDisplayOrders(sourceParentInfo.siblings);
        renumberDisplayOrders(destinationParentRef.children);

        return next;
      });

      return { previous };
    },
    onError: (error: { message?: string } | unknown, _input: unknown, ctx: { previous?: Location[] } | undefined) => {
      if (ctx?.previous) {
        utils.location.getTreeWithCounts.setData(locationsQueryInput, ctx.previous);
      }
      const message = (error as { message?: string } | null)?.message ?? '階層の移動に失敗しました';
      messageApi.error(message);
    },
    onSettled: async () => {
      await utils.location.getTreeWithCounts.invalidate(locationsQueryInput);
    },
  });
  const deleteMutation = trpc.location.delete.useMutation();
  const copyHierarchyMutation = trpc.location.copyHierarchy.useMutation();

  const { data: locationCategoriesData } = trpc.locationCategory.list.useQuery({
    includeInactive: true,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });
  const locationCategories = useMemo(
    () => normalizeLocationCategoryList(locationCategoriesData),
    [locationCategoriesData]
  );

  const locationCategoryOptions = useMemo(
    () =>
      locationCategories.map((category) => ({
        label: category.categoryLabel,
        value: category.id,
      })),
    [locationCategories]
  );

  const flatLocations = useMemo(
    () => getAllLocations(locationsTree),
    [locationsTree]
  );

  const objectCountById = useMemo(() => {
    const map = new Map<string, number>();

    const walk = (node: Location): { totalEquipments: number; totalParts: number } => {
      const children = node.children ?? [];

      let totalEquipments = node.equipmentCount ?? 0;
      let totalParts = node.partCount ?? 0;

      for (const child of children) {
        const childTotals = walk(child);
        totalEquipments += childTotals.totalEquipments;
        totalParts += childTotals.totalParts;
      }

      // バッジ表示: (設備数合計) + (部品・消耗品数合計) ※配下含む
      map.set(node.id, totalEquipments + totalParts);

      return { totalEquipments, totalParts };
    };

    locationsTree.forEach((root) => walk(root));

    return map;
  }, [locationsTree]);

  const locationById = useMemo(() => {
    const map = new Map<string, Location>();
    flatLocations.forEach((location) => map.set(location.id, location));
    return map;
  }, [flatLocations]);

  const rootLocationId = useMemo<string | null>(() => {
    const roots = locationsTree;
    return roots.length > 0 ? roots[0]?.id ?? null : null;
  }, [locationsTree]);

  // MainLayoutのリンク等で hash(scope) が無い場合でも、UI上は常にルートを表示する
  useEffect(() => {
    if (!selectedBranchId) {
      return;
    }
    if (hashState.scopeLocationId) {
      return;
    }
    if (!rootLocationId) {
      return;
    }
    if (mapScopeLocationId) {
      return;
    }

    setMapScopeLocationId(rootLocationId);
    setSelectedLocation(null);
  }, [hashState.scopeLocationId, mapScopeLocationId, rootLocationId, selectedBranchId]);

  const currentScopeMapMode = useMemo(() => {
    if (!mapScopeLocationId) {
      return 'image' as const;
    }
    return (locationById.get(mapScopeLocationId)?.mapMode ?? 'image') as 'image' | 'building' | 'folder' | 'geo';
  }, [locationById, mapScopeLocationId]);

  useEffect(() => {
    if (currentScopeMapMode !== 'geo' && isGeoClickPlaceMode) {
      setIsGeoClickPlaceMode(false);
    }
  }, [currentScopeMapMode, isGeoClickPlaceMode]);

  const isInCurrentScopeSubtree = useMemo(() => {
    const scopeId = mapScopeLocationId;
    if (!scopeId) {
      return (_candidateId?: string | null): boolean => false;
    }
    return (candidateId?: string | null): boolean => {
      if (!candidateId) return false;
      if (candidateId === scopeId) return true;

      const visited = new Set<string>();
      let current = locationById.get(candidateId) ?? null;
      while (current && !visited.has(current.id)) {
        visited.add(current.id);
        const parentId = current.parentId ?? null;
        if (!parentId) return false;
        if (parentId === scopeId) return true;
        current = locationById.get(parentId) ?? null;
      }
      return false;
    };
  }, [locationById, mapScopeLocationId]);

  const shouldShowGeoAddressFields = useMemo(() => {
    if (modalMapMode === 'geo') {
      return true;
    }
    if (currentScopeMapMode !== 'geo') {
      return false;
    }
    if (editingLocation?.id) {
      return isInCurrentScopeSubtree(editingLocation.id);
    }
    return isInCurrentScopeSubtree(modalParentId);
  }, [currentScopeMapMode, editingLocation?.id, isInCurrentScopeSubtree, modalMapMode, modalParentId]);

  const resolveInheritedGeoAddress = useCallback((parentId: string | null | undefined): string | null => {
    if (!parentId) {
      return null;
    }

    const visited = new Set<string>();
    let current = locationById.get(parentId) ?? null;
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      const address = typeof current.address === 'string' ? current.address.trim() : '';
      if (address) {
        return address;
      }
      current = current.parentId ? (locationById.get(current.parentId) ?? null) : null;
    }
    return null;
  }, [locationById]);

  // URL hash（scope/mode）を、該当データが揃ったタイミングで反映
  useEffect(() => {
    if (!isApplyingHashRef.current) {
      return;
    }

    const desiredScopeLocationId = hashState.scopeLocationId;
    if (!desiredScopeLocationId) {
      // UI assumes a branch always has a root location; if scope is not specified in URL, default to root.
      if (rootLocationId && locationById.has(rootLocationId) && mapScopeLocationId !== rootLocationId) {
        setMapScopeLocationId(rootLocationId);
        setSelectedLocation(null);
      } else if (!rootLocationId && mapScopeLocationId) {
        setMapScopeLocationId(null);
        setSelectedLocation(null);
      }
      return;
    }

    if (desiredScopeLocationId === mapScopeLocationId) {
      return;
    }

    if (!locationById.has(desiredScopeLocationId)) {
      return;
    }

    setMapScopeLocationId(desiredScopeLocationId);
    setSelectedLocation(null);
  }, [hashState.scopeLocationId, locationById, mapScopeLocationId, rootLocationId]);

  useEffect(() => {
    if (!isApplyingHashRef.current) {
      return;
    }

    const desiredMode = hashState.mapLocationsScopeMode ?? 'direct';
    if (desiredMode === mapLocationsScopeMode) {
      return;
    }
    setMapLocationsScopeMode(desiredMode);
  }, [hashState.mapLocationsScopeMode, mapLocationsScopeMode]);

  // hashで指定された状態が「反映済み」になったら、URL同期を解禁（ブラウザ戻る/進む時に上書きしないため）
  useEffect(() => {
    const desiredBranchId = hashState.branchId;
    const desiredScopeLocationId = hashState.scopeLocationId;
    const desiredMode = hashState.mapLocationsScopeMode ?? 'direct';

    const branchUnknown = Boolean(desiredBranchId) && Boolean(branchOptions.length) && !branchOptions.some((b) => b.id === desiredBranchId);
    const branchResolved = !desiredBranchId || desiredBranchId === selectedBranchId || branchUnknown;
    const modeResolved = desiredMode === mapLocationsScopeMode;

    let scopeResolved = true;
    if (desiredScopeLocationId) {
      if (desiredScopeLocationId === mapScopeLocationId) {
        scopeResolved = true;
      } else if (!isLoading && !locationById.has(desiredScopeLocationId)) {
        // 該当エリアが存在しない場合は適用不能として扱う（無限に同期が止まるのを防ぐ）
        scopeResolved = true;
      } else {
        scopeResolved = false;
      }
    } else {
      // No scope in URL => default to root if present (prevents "階層0" UI state).
      if (rootLocationId) {
        scopeResolved = mapScopeLocationId === rootLocationId;
      } else {
        scopeResolved = !mapScopeLocationId;
      }
    }

    if (branchResolved && modeResolved && scopeResolved) {
      isApplyingHashRef.current = false;
    }
  }, [
    branchOptions,
    hashState.branchId,
    hashState.mapLocationsScopeMode,
    hashState.scopeLocationId,
    isLoading,
    locationById,
    mapLocationsScopeMode,
    mapScopeLocationId,
    selectedBranchId,
    rootLocationId,
  ]);

  // 状態（選択ブランチ/スコープ/表示モード）をURL hashに同期（シェア可能なURLにする）
  useEffect(() => {
    if (!selectedBranchId) {
      return;
    }

    if (isApplyingHashRef.current) {
      return;
    }

    const scopeLocationIdForHash =
      rootLocationId && mapScopeLocationId === rootLocationId ? null : mapScopeLocationId;

    const nextHash = buildLocationsHash({
      branchId: selectedBranchId,
      scopeLocationId: scopeLocationIdForHash,
      mapLocationsScopeMode,
    });

    if (routerLocation.hash === nextHash) {
      return;
    }

    const currentRawHash = routerLocation.hash;
    const currentTrimmed = currentRawHash.startsWith('#') ? currentRawHash.slice(1) : currentRawHash;
    const isLegacyHash = Boolean(currentTrimmed) && !currentTrimmed.includes('=') && !currentTrimmed.includes('&');
    const currentParsed = parseLocationsHash(currentRawHash);
    const shouldReplace = isLegacyHash || !currentParsed.branchId;

    navigate({ hash: nextHash }, { replace: shouldReplace });
  }, [
    mapLocationsScopeMode,
    mapScopeLocationId,
    navigate,
    routerLocation.hash,
    selectedBranchId,
    rootLocationId,
  ]);

  const scopeLocation = useMemo(() => {
    if (!mapScopeLocationId) return null;
    return locationById.get(mapScopeLocationId) ?? null;
  }, [locationById, mapScopeLocationId]);

  const assetTargetLocationId =
    selectedLocation?.id ?? scopeLocation?.id ?? mapScopeLocationId ?? null;

  const isPositionLocked = scopeLocation
    ? (scopeLocation.isPositionLocked ?? true)
    : false;
  const isBulkPositionEditMode = canManage && !isPositionLocked;

  const scopeLocationChain = useMemo(() => {
    if (!mapScopeLocationId) return [];
    const chain: Location[] = [];
    const visited = new Set<string>();
    let current = locationById.get(mapScopeLocationId) ?? null;
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      chain.push(current);
      current = current.parentId ? (locationById.get(current.parentId) ?? null) : null;
    }

    const reversed = chain.reverse();
    // 先頭の「支店ルート(/root)」はUI上は拠点(パンくず先頭)で表現するため、二重表示を避けて除外する
    if (rootLocationId && reversed.length > 0 && reversed[0]?.id === rootLocationId) {
      return reversed.slice(1);
    }

    return reversed;
  }, [locationById, mapScopeLocationId, rootLocationId]);

  const buildingContext = useMemo(() => {
    if (!mapScopeLocationId) {
      return {
        building: null as Location | null,
        floors: [] as Location[],
        activeFloorId: null as string | null,
        isBuildingRootScope: false,
      };
    }

    const scope = locationById.get(mapScopeLocationId) ?? null;
    if (!scope) {
      return {
        building: null as Location | null,
        floors: [] as Location[],
        activeFloorId: null as string | null,
        isBuildingRootScope: false,
      };
    }

    if (scope.mapMode === 'building') {
      const floors = (scope.children ?? [])
        .slice()
        .sort((a, b) => {
          const orderA = a.displayOrder ?? 0;
          const orderB = b.displayOrder ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name, 'ja');
        });
      return {
        building: scope,
        floors,
        activeFloorId: null,
        isBuildingRootScope: true,
      };
    }

    const parent = scope.parentId ? (locationById.get(scope.parentId) ?? null) : null;
    if (parent?.mapMode === 'building') {
      const floors = (parent.children ?? [])
        .slice()
        .sort((a, b) => {
          const orderA = a.displayOrder ?? 0;
          const orderB = b.displayOrder ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          return a.name.localeCompare(b.name, 'ja');
        });
      return {
        building: parent,
        floors,
        activeFloorId: scope.id,
        isBuildingRootScope: false,
      };
    }

    return {
      building: null as Location | null,
      floors: [] as Location[],
      activeFloorId: null as string | null,
      isBuildingRootScope: false,
    };
  }, [locationById, mapScopeLocationId]);

  const isBuildingFloorScope = Boolean(buildingContext.building) && !buildingContext.isBuildingRootScope;

  // 建物モード（フロア）に入ったときは「全体（= このマップ上の全体 / 直下）」をデフォルト選択にする
  const prevIsBuildingFloorScopeRef = useRef(false);
  useEffect(() => {
    const prev = prevIsBuildingFloorScopeRef.current;
    if (!prev && isBuildingFloorScope) {
      setMapLocationsScopeMode('direct');
    }
    if (prev && !isBuildingFloorScope) {
      setMapLocationsScopeMode('direct');
    }
    prevIsBuildingFloorScopeRef.current = isBuildingFloorScope;
  }, [isBuildingFloorScope]);

  // 表示範囲切替は、建物（フロア）/地理地図でのみ有効。その他へ移動したら直下に戻す。
  useEffect(() => {
    if (isBuildingFloorScope || currentScopeMapMode === 'geo') {
      return;
    }
    if (mapLocationsScopeMode !== 'direct') {
      setMapLocationsScopeMode('direct');
    }
  }, [currentScopeMapMode, isBuildingFloorScope, mapLocationsScopeMode]);

  const buildingFloorsForNavigator = useMemo(() => {
    if (!buildingContext.building) return [];
    return buildingContext.floors.map((floor) => ({
      id: floor.id,
      name: floor.name,
      objectCount: objectCountById.get(floor.id) ?? 0,
      displayOrder: floor.displayOrder ?? 0,
    }));
  }, [buildingContext.building, buildingContext.floors, objectCountById]);

  const activeFloorLabel = useMemo(() => {
    if (!buildingContext.building || !buildingContext.activeFloorId) return null;
    const idx = buildingContext.floors.findIndex((f) => f.id === buildingContext.activeFloorId);
    const floor = idx >= 0 ? buildingContext.floors[idx] : null;
    const displayOrder = floor?.displayOrder;
    const value =
      typeof displayOrder === 'number' && Number.isFinite(displayOrder) ? displayOrder : idx + 1;
    if (value < 0) return `B${Math.abs(value)}F`;
    return `${value}F`;
  }, [buildingContext.activeFloorId, buildingContext.building, buildingContext.floors]);

  const scopedMapLocations = useMemo(() => {
    if (!mapScopeLocationId) {
      return flatLocations.filter((location) => location.parentId === null);
    }
    const allowAll = isBuildingFloorScope || currentScopeMapMode === 'geo';
    if (!allowAll || mapLocationsScopeMode === 'direct') {
      return flatLocations.filter((location) => location.parentId === mapScopeLocationId);
    }

    const scope = locationById.get(mapScopeLocationId) ?? null;
    if (!scope?.children || scope.children.length === 0) {
      return [];
    }

    const result: Location[] = [];
    const stack: Location[] = [...scope.children];
    while (stack.length > 0) {
      const node = stack.pop();
      if (!node) continue;
      result.push(node);
      if (node.children && node.children.length > 0) {
        stack.push(...node.children);
      }
    }
    return result;
  }, [currentScopeMapMode, flatLocations, isBuildingFloorScope, locationById, mapLocationsScopeMode, mapScopeLocationId]);

  const selectableLocationIdSet = useMemo(() => {
    const set = new Set<string>();
    scopedMapLocations.forEach((location) => set.add(location.id));
    // 地理地図では「現在のスコープ（= 親エリア）」自体も選択対象として扱う
    if (currentScopeMapMode === 'geo' && mapScopeLocationId) {
      set.add(mapScopeLocationId);
    }
    return set;
  }, [currentScopeMapMode, mapScopeLocationId, scopedMapLocations]);

  useEffect(() => {
    if (selectedLocation && !selectableLocationIdSet.has(selectedLocation.id)) {
      setSelectedLocation(null);
    }
  }, [selectableLocationIdSet, selectedLocation]);

  const locationCategoryById = useMemo(() => {
    const map = new Map<string, LocationCategory>();
    locationCategories.forEach((category) => map.set(category.id, category));
    return map;
  }, [locationCategories]);
  const selectedCategory = useMemo(() => {
    if (!selectedLocation?.categoryId) return undefined;
    return locationCategoryById.get(selectedLocation.categoryId);
  }, [locationCategoryById, selectedLocation?.categoryId]);
  const selectedLocationIsBuilding = useMemo(() => {
    if (!selectedLocation) return false;
    return Boolean(selectedCategory?.isBuilding || selectedLocation.mapMode === 'building');
  }, [selectedCategory?.isBuilding, selectedLocation]);
  const assetTargetCategory = useMemo(() => {
    if (!assetTargetLocationId) return undefined;
    const location = locationById.get(assetTargetLocationId) ?? null;
    if (!location?.categoryId) return undefined;
    return locationCategoryById.get(location.categoryId);
  }, [assetTargetLocationId, locationById, locationCategoryById]);
  const assetTargetLocationIsBuilding = useMemo(() => {
    if (!assetTargetLocationId) return false;
    const location = locationById.get(assetTargetLocationId) ?? null;
    if (!location) return false;
    return Boolean(location.mapMode === 'building' || assetTargetCategory?.isBuilding);
  }, [assetTargetCategory?.isBuilding, assetTargetLocationId, locationById]);

  const thumbnailFileIds = useMemo(() => {
    const ids = scopedMapLocations
      .map((location) => location.thumbnailFileId)
      .filter((value): value is string => typeof value === 'string' && value.length > 0);
    return Array.from(new Set(ids));
  }, [scopedMapLocations]);

  const { data: thumbnailDownloadUrls } = trpc.file.getDownloadUrls.useQuery(
    { fileIds: thumbnailFileIds, action: 'view' },
    { enabled: thumbnailFileIds.length > 0 }
  );

  const geoViewLocations = useMemo(() => {
    if (currentScopeMapMode !== 'geo') {
      return [] as Location[];
    }
    return [...scopedMapLocations];
  }, [currentScopeMapMode, scopedMapLocations]);

  const geoScopeCoordinates = useMemo(() => {
    if (currentScopeMapMode !== 'geo') {
      return null as { latitude: string | null; longitude: string | null } | null;
    }
    if (!mapScopeLocationId) {
      return null;
    }
    const scope = locationById.get(mapScopeLocationId) ?? null;
    if (!scope) {
      return null;
    }
    return {
      latitude: (scope.pinLatitude ?? scope.latitude) ?? null,
      longitude: (scope.pinLongitude ?? scope.longitude) ?? null,
    };
  }, [currentScopeMapMode, locationById, mapScopeLocationId]);

  const geoScopeCenter = useMemo(() => {
    const parsed = parseLatLon(geoScopeCoordinates?.latitude ?? null, geoScopeCoordinates?.longitude ?? null);
    if (!parsed) return null as { lat: number; lon: number } | null;
    return parsed;
  }, [geoScopeCoordinates?.latitude, geoScopeCoordinates?.longitude]);

  const geoDisplayLocations = useMemo(() => {
    if (currentScopeMapMode !== 'geo') {
      return [] as Array<{
        id: string;
        name: string;
        latitude: string | number | null;
        longitude: string | number | null;
        description: string | null;
        categoryIcon: string | null;
        categoryColor: string | null;
        isVirtualCoordinates: boolean;
      }>;
    }

    const getRealCenter = (location: Location): { lat: number; lon: number } | null => {
      const latValue = (location.pinLatitude ?? location.latitude) ?? null;
      const lonValue = (location.pinLongitude ?? location.longitude) ?? null;
      return parseLatLon(latValue, lonValue);
    };

    const locationsSorted = [...geoViewLocations].sort((a, b) => a.id.localeCompare(b.id));
    const missingByAnchorKey = new Map<string, string[]>();

    for (const location of locationsSorted) {
      if (getRealCenter(location)) {
        continue;
      }

      let anchorKey = 'scope';
      let anchorCenter = geoScopeCenter;

      // 直近の祖先で「実座標あり」を見つけたら、その周りに仮配置する
      let parent = location.parentId ? (locationById.get(location.parentId) ?? null) : null;
      while (parent) {
        const parentCenter = getRealCenter(parent);
        if (parentCenter) {
          anchorKey = parent.id;
          anchorCenter = parentCenter;
          break;
        }
        parent = parent.parentId ? (locationById.get(parent.parentId) ?? null) : null;
      }

      if (!anchorCenter) {
        continue;
      }
      const list = missingByAnchorKey.get(anchorKey) ?? [];
      list.push(location.id);
      missingByAnchorKey.set(anchorKey, list);
    }

    const missingIndexById = new Map<string, number>();
    for (const [anchorKey, ids] of missingByAnchorKey.entries()) {
      const sorted = [...ids].sort((a, b) => a.localeCompare(b));
      sorted.forEach((id, index) => missingIndexById.set(`${anchorKey}:${id}`, index));
    }

    return locationsSorted.map((location) => {
      const latValue = (location.pinLatitude ?? location.latitude) ?? null;
      const lonValue = (location.pinLongitude ?? location.longitude) ?? null;
      const hasRealCoordinates = getRealCenter(location) !== null;

      const category = location.categoryId ? locationCategoryById.get(location.categoryId) : undefined;

      if (hasRealCoordinates) {
        return {
          id: location.id,
          name: location.name,
          latitude: latValue,
          longitude: lonValue,
          description: location.description ?? null,
          categoryIcon: category?.icon ?? null,
          categoryColor: category?.color ?? null,
          isVirtualCoordinates: false,
        };
      }

      let anchorKey = 'scope';
      let anchorCenter = geoScopeCenter;
      let parent = location.parentId ? (locationById.get(location.parentId) ?? null) : null;
      while (parent) {
        const parentCenter = getRealCenter(parent);
        if (parentCenter) {
          anchorKey = parent.id;
          anchorCenter = parentCenter;
          break;
        }
        parent = parent.parentId ? (locationById.get(parent.parentId) ?? null) : null;
      }

      if (anchorCenter) {
        const index = missingIndexById.get(`${anchorKey}:${location.id}`) ?? 0;
        const next = computeVirtualLatLon(anchorCenter, index);
        return {
          id: location.id,
          name: location.name,
          latitude: next.lat,
          longitude: next.lon,
          description: location.description ?? null,
          categoryIcon: category?.icon ?? null,
          categoryColor: category?.color ?? null,
          isVirtualCoordinates: true,
        };
      }

      return {
        id: location.id,
        name: location.name,
        latitude: null,
        longitude: null,
        description: location.description ?? null,
        categoryIcon: category?.icon ?? null,
        categoryColor: category?.color ?? null,
        isVirtualCoordinates: false,
      };
    });
  }, [currentScopeMapMode, geoScopeCenter, geoViewLocations, locationById, locationCategoryById]);

  const geoViewLocationStats = useMemo(() => {
    const total = geoViewLocations.length;
    const withCoordinates = geoViewLocations.filter((location) => {
      const latValue = (location.pinLatitude ?? location.latitude) ?? null;
      const lonValue = (location.pinLongitude ?? location.longitude) ?? null;
      return parseLatLon(latValue, lonValue) !== null;
    }).length;
    const virtualPinned = geoScopeCenter ? (total - withCoordinates) : 0;
    return { total, withCoordinates, virtualPinned };
  }, [geoScopeCenter, geoViewLocations]);

  const formattedGeoScopeCoordinates = useMemo(() => {
    if (!geoScopeCoordinates) return null;
    const parsed = parseLatLon(geoScopeCoordinates.latitude, geoScopeCoordinates.longitude);
    if (!parsed) return '未設定';
    return `${parsed.lat.toFixed(6)}, ${parsed.lon.toFixed(6)}`;
  }, [geoScopeCoordinates]);

  const thumbnailUrlByFileId = useMemo(() => {
    const map: Record<string, string> = {};
    (thumbnailDownloadUrls?.items ?? []).forEach((item) => {
      if (typeof item.url === 'string' && item.url.length > 0) {
        map[item.fileId] = item.url;
      }
    });
    return map;
  }, [thumbnailDownloadUrls?.items]);

  const handleMoveLocationOnMap = useCallback(
    async (locationId: string, mapX: number, mapY: number) => {
      const previousTree = utils.location.getTreeWithCounts.getData(locationsQueryInput);
      if (previousTree) {
        utils.location.getTreeWithCounts.setData(locationsQueryInput, (current) => {
          if (!current) return current;
          const apply = (nodes: Location[]): Location[] =>
            nodes.map((node) => ({
              ...node,
              ...(node.id === locationId ? { mapX, mapY } : {}),
              children: node.children ? apply(node.children) : node.children,
            }));
          return apply(current as unknown as Location[]);
        });
      }

      try {
        await updateMutation.mutateAsync({
          id: locationId,
          data: { mapX, mapY },
        });
      } catch (error) {
        logger.error('[LocationsPage] Failed to update location coordinates', { locationId, error });
        messageApi.error('位置の更新に失敗しました');
        if (previousTree) {
          utils.location.getTreeWithCounts.setData(locationsQueryInput, previousTree);
        } else {
          await refetch();
        }
      }
    },
    [locationsQueryInput, messageApi, refetch, updateMutation, utils.location.getTreeWithCounts]
  );

  const handleMoveGeoLocationOnMap = useCallback(
    async (locationId: string, latitude: number, longitude: number) => {
      const lat = Number.isFinite(latitude) ? latitude.toFixed(6) : null;
      const lon = Number.isFinite(longitude) ? longitude.toFixed(6) : null;
      if (!lat || !lon) {
        return;
      }

      const previousTree = utils.location.getTreeWithCounts.getData(locationsQueryInput);
      if (previousTree) {
        utils.location.getTreeWithCounts.setData(locationsQueryInput, (current) => {
          if (!current) return current;
          const apply = (nodes: Location[]): Location[] =>
            nodes.map((node) => ({
              ...node,
              ...(node.id === locationId ? { pinLatitude: lat, pinLongitude: lon } : {}),
              children: node.children ? apply(node.children) : node.children,
            }));
          return apply(current as unknown as Location[]);
        });
      }

      if (selectedLocation?.id === locationId) {
        setSelectedLocation({ ...selectedLocation, pinLatitude: lat, pinLongitude: lon });
      }

      try {
        await updateMutation.mutateAsync({
          id: locationId,
          data: { pinLatitude: lat, pinLongitude: lon },
        });
        messageApi.success({ content: '位置を更新しました', key: 'geo-location-position', duration: 1 });
        messageApi.info({ content: '住所は自動更新されません（必要なら住所検索で更新してください）', key: 'geo-location-address', duration: 2 });
      } catch (error) {
        logger.error('[LocationsPage] Failed to update geo coordinates', { locationId, error });
        messageApi.error('位置の更新に失敗しました');
        if (previousTree) {
          utils.location.getTreeWithCounts.setData(locationsQueryInput, previousTree);
        } else {
          await refetch();
        }
      }
    },
    [
      locationsQueryInput,
      messageApi,
      refetch,
      selectedLocation,
      updateMutation,
      utils.location.getTreeWithCounts,
    ]
  );

  const handleQuickCreateAt = useCallback((mapX: number, mapY: number) => {
    if (!canManage) {
      return;
    }

    setEditingLocation(null);
    setPendingCreateCoordinates({ mapX, mapY });
    form.resetFields();

    const scopeLocation = mapScopeLocationId ? (locationById.get(mapScopeLocationId) ?? null) : null;
    if (scopeLocation) {
      const inheritedAddress = scopeLocation ? resolveInheritedGeoAddress(scopeLocation.id) : null;
      form.setFieldsValue({
        parentId: scopeLocation.id,
        ...(currentScopeMapMode === 'geo'
          ? {
            address: inheritedAddress ?? undefined,
            latitude: undefined,
            longitude: undefined,
          }
          : {}),
      });
      setParentLocationName(scopeLocation.name);
    } else {
      form.setFieldsValue({ parentId: null });
      setParentLocationName(null);
    }

    setIsModalVisible(true);
  }, [canManage, currentScopeMapMode, form, locationById, mapScopeLocationId, resolveInheritedGeoAddress]);

  const handleTogglePositionLocked = useCallback(async (nextLocked: boolean) => {
    if (!mapScopeLocationId) {
      return;
    }

    const previousTree = utils.location.getTreeWithCounts.getData(locationsQueryInput);
    if (previousTree) {
      utils.location.getTreeWithCounts.setData(locationsQueryInput, (current) => {
        if (!current) return current;
        const apply = (nodes: Location[]): Location[] =>
          nodes.map((node) => ({
            ...node,
            ...(node.id === mapScopeLocationId ? { isPositionLocked: nextLocked } : {}),
            children: node.children ? apply(node.children) : node.children,
          }));
        return apply(current as unknown as Location[]);
      });
    }

    try {
      await updateMutation.mutateAsync({
        id: mapScopeLocationId,
        data: { isPositionLocked: nextLocked },
      });
      messageApi.success(nextLocked ? '位置を固定しました' : '位置固定を解除しました');
    } catch (error) {
      logger.error('[LocationsPage] Failed to update isPositionLocked', { mapScopeLocationId, error });
      messageApi.error('位置固定設定の更新に失敗しました');
      if (previousTree) {
        utils.location.getTreeWithCounts.setData(locationsQueryInput, previousTree);
      } else {
        await refetch();
      }
    }
  }, [locationsQueryInput, mapScopeLocationId, messageApi, refetch, updateMutation, utils.location.getTreeWithCounts]);

  const handleChangeSelectedLocationCategory = useCallback(async (nextCategoryId: string | undefined) => {
    if (!canManage || getMutationPendingState(updateMutation)) {
      return;
    }
    if (!selectedLocation) {
      return;
    }

    const locationId = selectedLocation.id;
    const previousCategoryId = selectedLocation.categoryId ?? null;
    const normalizedCategoryId = nextCategoryId ?? null;

    setSelectedLocation((prev) => (prev && prev.id === locationId ? { ...prev, categoryId: normalizedCategoryId } : prev));

    const previousTree = utils.location.getTreeWithCounts.getData(locationsQueryInput);
    if (previousTree) {
      utils.location.getTreeWithCounts.setData(locationsQueryInput, (current) => {
        if (!current) return current;
        const apply = (nodes: Location[]): Location[] =>
          nodes.map((node) => ({
            ...node,
            ...(node.id === locationId ? { categoryId: normalizedCategoryId } : {}),
            children: node.children ? apply(node.children) : node.children,
          }));
        return apply(current as unknown as Location[]);
      });
    }

    try {
      await updateMutation.mutateAsync({
        id: locationId,
        data: { categoryId: normalizedCategoryId },
      });
      messageApi.success('カテゴリを更新しました');
    } catch (error) {
      logger.error('[LocationsPage] Failed to update categoryId', { locationId, error });
      messageApi.error('カテゴリの更新に失敗しました');
      setSelectedLocation((prev) => (prev && prev.id === locationId ? { ...prev, categoryId: previousCategoryId } : prev));
      if (previousTree) {
        utils.location.getTreeWithCounts.setData(locationsQueryInput, previousTree);
      } else {
        await refetch();
      }
    }
  }, [canManage, locationsQueryInput, messageApi, refetch, selectedLocation, updateMutation, utils.location.getTreeWithCounts]);

  const handleMoveLocationsOnMap = useCallback(
    async (updates: Array<{ id: string; x: number; y: number }>) => {
      if (updates.length === 0) {
        return;
      }

      const previousTree = utils.location.getTreeWithCounts.getData(locationsQueryInput);
      if (previousTree) {
        const updateMap = new Map(updates.map((u) => [u.id, { mapX: u.x, mapY: u.y }] as const));
        utils.location.getTreeWithCounts.setData(locationsQueryInput, (current) => {
          if (!current) return current;
          const apply = (nodes: Location[]): Location[] =>
            nodes.map((node) => {
              const next = updateMap.get(node.id);
              return {
                ...node,
                ...(next ? next : {}),
                children: node.children ? apply(node.children) : node.children,
              };
            });
          return apply(current as unknown as Location[]);
        });
      }

      messageApi.loading({ content: '位置を更新しています...', key: 'bulk-location-position' });

      let succeeded = 0;
      let failed = 0;

      const batchSize = 8;
      for (let start = 0; start < updates.length; start += batchSize) {
        const batch = updates.slice(start, start + batchSize);
        const results = await Promise.allSettled(
          batch.map((update) =>
            updateMutation.mutateAsync({
              id: update.id,
              data: { mapX: update.x, mapY: update.y },
            })
          )
        );

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            succeeded += 1;
          } else {
            failed += 1;
            logger.error('[LocationsPage] Failed to update location coordinates (bulk)', {
              update: batch[index],
              error: result.reason,
            });
          }
        });
      }

      if (failed === 0) {
        messageApi.success({ content: `位置を更新しました（${succeeded}件）`, key: 'bulk-location-position', duration: 2 });
      } else {
        messageApi.warning({ content: `位置の更新に失敗しました（成功: ${succeeded} / 失敗: ${failed}）`, key: 'bulk-location-position', duration: 3 });
      }

      if (failed > 0) {
        // 失敗が混じった場合は整合のために再取得
        if (previousTree) {
          // 一旦元に戻してから再取得（部分的に更新された場合のブレを避ける）
          utils.location.getTreeWithCounts.setData(locationsQueryInput, previousTree);
        }
        await refetch();
      }
    },
    [locationsQueryInput, messageApi, refetch, updateMutation, utils.location.getTreeWithCounts]
  );

  // 設備作成mutation
  const equipmentCreateMutation = trpc.equipment.create.useMutation({
    onSuccess: () => {
      messageApi.success('設備を作成しました');
      setIsEquipmentModalVisible(false);
      setEquipmentCreateCategoryId(null);
      refetch();
      if (mapScopeLocationId && companyId) {
        refetchEquipment();
        refetchParts();
      }
    },
    onError: (error) => {
      messageApi.error(`設備の作成に失敗しました: ${error.message}`);
    },
  });

  // エラーログ出力
  useEffect(() => {
    if (error) {
      console.error('Location API Error:', error);
      messageApi.error(`データの取得に失敗しました: ${error.message}`);
    }
  }, [error, messageApi]);

  // branchIdが変更されたときに選択をクリアしてデータを再取得
  useEffect(() => {
    if (selectedBranchId) {
      setSelectedLocation(null);
      refetch();
    }
  }, [selectedBranchId, refetch]);


  // 全てのエリアIDを収集する関数
  const collectAllLocationIds = useCallback((locations: Location[]): string[] => {
    return locations.reduce<string[]>((acc, location) => {
      const childrenIds = location.children ? collectAllLocationIds(location.children) : [];
      return [...acc, location.id, ...childrenIds];
    }, []);
  }, []);

  // 初期展開キーを設定（全階層を展開）
  useEffect(() => {
    if (locationsTree && locationsTree.length > 0) {
      const allIds = collectAllLocationIds(locationsTree);
      setExpandedKeys(allIds);
    }
  }, [collectAllLocationIds, locationsTree]);

  // アイコンコンポーネントを取得
  const getIconComponent = (iconName?: string | null) => {
    if (!iconName || !(iconName in Icons)) {
      return null;
    }
    const iconMap = Icons as unknown as Record<string, AntIconComponent>;
    const IconComponent = iconMap[iconName];
    if (!IconComponent) {
      return null;
    }
    return <IconComponent style={{ fontSize: 16 }} />;
  };

  const convertToTreeData = (locations: Location[]): DataNode[] => {
    if (!locations || locations.length === 0) {
      return [];
    }
    return locations.map((location) => ({
      title: (
        <span className="location-tree-title">
          <span className="location-tree-title__text">{location.name}</span>
          {(() => {
            const equipmentCount = location.equipmentCount ?? 0;
            const partCount = location.partCount ?? 0;
            const totalCount = equipmentCount + partCount;
            if (totalCount <= 0) {
              return null;
            }
            return (
              <Tag className="location-tree-title__meta" style={{ marginRight: 0, fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>
                {totalCount}
              </Tag>
            );
          })()}
        </span>
      ),
      key: location.id,
      icon: (() => {
        const categoryIconName = location.categoryId
          ? locationCategoryById.get(location.categoryId)?.icon
          : null;

        const categoryIcon = categoryIconName ? getIconComponent(categoryIconName) : null;
        if (categoryIcon) {
          return categoryIcon;
        }

        const customIcon = location.icon ? getIconComponent(location.icon) : null;
        if (customIcon) {
          return customIcon;
        }

        if (location.mapMode === 'building') return <ApartmentOutlined />;
        if (location.mapMode === 'folder') return <FolderOutlined />;
        return location.children && location.children.length > 0 ? <FolderOutlined /> : <EnvironmentOutlined />;
      })(),
      children: location.children ? convertToTreeData(location.children) : undefined,
    }));
  };

  const treeSourceLocations = useMemo(() => {
    const roots = locationsTree;
    if (!roots.length) return [] as Location[];

    // 階層パネルには「支店ルート(/root)」も表示して、ルートへの遷移手段を常に提供する
    return roots;
  }, [locationsTree]);

  const treeData = treeSourceLocations.length > 0 ? convertToTreeData(treeSourceLocations) : [];

  const findLocation = (id: string, locations: Location[]): Location | null => {
    for (const location of locations) {
      if (location.id === id) {
        return location;
      }
      if (location.children) {
        const found = findLocation(id, location.children);
        if (found) return found;
      }
    }
    return null;
  };

  const onSelect = (selectedKeys: Key[]) => {
    if (selectedKeys.length > 0 && locationsTree) {
      const selectedKey = String(selectedKeys[0]);
      setMapScopeLocationId(selectedKey);
      const resolved = locationById.get(selectedKey) ?? findLocation(selectedKey, locationsTree);
      setSelectedLocation(resolved);
    }
  };

  const isLocationDescendant = useCallback((ancestorId: string, maybeDescendantId: string): boolean => {
    if (ancestorId === maybeDescendantId) return true;
    const start = locationById.get(ancestorId) ?? null;
    if (!start?.children || start.children.length === 0) return false;
    const stack = [...start.children.map((c) => c.id)];
    while (stack.length > 0) {
      const currentId = stack.pop();
      if (!currentId) break;
      if (currentId === maybeDescendantId) return true;
      const node = locationById.get(currentId) ?? null;
      const children = node?.children ?? [];
      for (const child of children) {
        stack.push(child.id);
      }
    }
    return false;
  }, [locationById]);

  const handleHierarchyDrop = useCallback(async (info: TreeDropInfo) => {
    if (!canManage || !isHierarchyMoveEnabled) {
      return;
    }
    if (!rootLocationId) {
      return;
    }

    const dragId = String(info?.dragNode?.key ?? '');
    const dropId = String(info?.node?.key ?? '');
    if (!dragId || !dropId) return;

    if (dragId === rootLocationId) return;
    if (dragId === dropId) return;
    if (isLocationDescendant(dragId, dropId)) return;

    const dragLocation = locationById.get(dragId) ?? null;
    if (!dragLocation) return;

    const dropToGap = Boolean(info?.dropToGap);

    let destinationParentId: string | null = null;
    let destinationIndex = 0;

    if (!dropToGap) {
      destinationParentId = dropId;
      const destParent = locationById.get(destinationParentId) ?? null;
      const children = (destParent?.children ?? []).map((c) => c.id).filter((id) => id !== dragId);
      destinationIndex = children.length;
    } else {
      const dropLocation = locationById.get(dropId) ?? null;
      destinationParentId = dropLocation?.parentId ?? null;
      if (!destinationParentId) {
        return;
      }

      // dropping to gap around root itself is not a meaningful ordering; force into-root only.
      if (dropId === rootLocationId) {
        return;
      }

      const siblings = (locationById.get(destinationParentId)?.children ?? []).map((c) => c.id);
      const siblingsWithoutDrag = siblings.filter((id) => id !== dragId);
      const dropIndex = siblingsWithoutDrag.indexOf(dropId);
      if (dropIndex < 0) {
        return;
      }

      const posParts = String(info?.node?.pos ?? '').split('-');
      const nodeIndex = Number(posParts[posParts.length - 1]);
      const relativeDropPosition = Number(info?.dropPosition) - nodeIndex;
      destinationIndex = dropIndex + (relativeDropPosition > 0 ? 1 : 0);

      // no-op guard for reordering within same parent
      if (destinationParentId === dragLocation.parentId) {
        const currentIds = (locationById.get(destinationParentId)?.children ?? []).map((c) => c.id);
        const filtered = currentIds.filter((id) => id !== dragId);
        const nextIds = filtered.slice();
        const clamped = Math.max(0, Math.min(destinationIndex, nextIds.length));
        nextIds.splice(clamped, 0, dragId);
        if (nextIds.join('|') === currentIds.join('|')) {
          return;
        }
      }
    }

    if (!destinationParentId) return;

    moveInTreeMutation.mutate({
      locationId: dragId,
      destinationParentId,
      destinationIndex,
      expectedUpdatedAt: dragLocation.updatedAt ?? null,
    });
  }, [canManage, isHierarchyMoveEnabled, isLocationDescendant, locationById, moveInTreeMutation, rootLocationId]);

  const selectedTreeKeys = useMemo<string[]>(() => {
    // 階層パネルのフォーカスは「現在のマップのスコープ」に固定する。
    // マップ上でロケーションを選択しても、階層パネルの強調表示は移動させない。
    if (mapScopeLocationId) return [mapScopeLocationId];
    if (rootLocationId) return [rootLocationId];
    return [];
  }, [mapScopeLocationId, rootLocationId]);

  const rootLocation = useMemo<Location | null>(() => {
    if (!rootLocationId) return null;
    return locationById.get(rootLocationId) ?? null;
  }, [locationById, rootLocationId]);

  const scopeLocationForTitle = useMemo<Location | null>(() => {
    if (!mapScopeLocationId) return null;
    return locationById.get(mapScopeLocationId) ?? null;
  }, [locationById, mapScopeLocationId]);

  const handleScopeUp = () => {
    if (!mapScopeLocationId) return;
    const current = locationById.get(mapScopeLocationId) ?? null;
    const nextScopeId = current?.parentId ?? null;
    if (!nextScopeId) {
      // Keep the scope anchored at root (avoid falling back to "階層0" UI state)
      if (rootLocationId && mapScopeLocationId !== rootLocationId) {
        setMapScopeLocationId(rootLocationId);
        setSelectedLocation(null);
      }
      return;
    }
    setMapScopeLocationId(nextScopeId);
    setSelectedLocation(locationById.get(nextScopeId) ?? null);
  };

  const handleAdd = (parent?: Location) => {
    setEditingLocation(null);
    setPendingCreateCoordinates(null);
    setGeocodeCandidates([]);
    form.resetFields();
    if (parent) {
      const inheritedAddress = currentScopeMapMode === 'geo'
        ? resolveInheritedGeoAddress(parent.id)
        : null;
      form.setFieldsValue({
        parentId: parent.id,
        address: inheritedAddress ?? undefined,
        latitude: undefined,
        longitude: undefined,
      });
      setParentLocationName(parent.name);
    } else {
      form.setFieldsValue({
        parentId: null
      });
      setParentLocationName(null);
    }
    setIsModalVisible(true);
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setPendingCreateCoordinates(null);
    setGeocodeCandidates([]);
    setParentLocationName(null);
    setIsModalVisible(true);
  };

  // 編集モーダルが開いたときにフォームの値を設定
  useEffect(() => {
    if (isModalVisible && editingLocation) {
      form.setFieldsValue({
        name: editingLocation.name,
        description: editingLocation.description ?? '',
        categoryId: editingLocation.categoryId ?? undefined,
        parentId: editingLocation.parentId ?? undefined,
        mapMode: editingLocation.mapMode ?? 'image',
        address: editingLocation.address ?? undefined,
        latitude: editingLocation.latitude ?? undefined,
        longitude: editingLocation.longitude ?? undefined,
        pinLatitude: editingLocation.pinLatitude ?? undefined,
        pinLongitude: editingLocation.pinLongitude ?? undefined,
        displayOrder: typeof editingLocation.displayOrder === 'number' ? editingLocation.displayOrder : 0,
      });
      setGeocodeCandidates([]);
    }
    // form は Form.useForm() から取得した安定したインスタンスなので依存配列から除外
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalVisible, editingLocation]);

  const getLocationDeleteBlockedReason = (location: Location): string | null => {
    if (location.children && location.children.length > 0) {
      return '子エリアが存在するため削除できません';
    }
    const equipmentCount = location.equipmentCount ?? 0;
    if (equipmentCount > 0) {
      return '設備が存在するため削除できません';
    }
    const partCount = location.partCount ?? 0;
    if (partCount > 0) {
      return '部品・消耗品が存在するため削除できません';
    }
    return null;
  };

  const selectedLocationForDelete = selectedLocation
    ? (locationById.get(selectedLocation.id) ?? selectedLocation)
    : null;

  const selectedLocationDeleteBlockedReason = selectedLocationForDelete
    ? getLocationDeleteBlockedReason(selectedLocationForDelete)
    : null;

  const editingLocationForDelete = editingLocation
    ? (locationById.get(editingLocation.id) ?? editingLocation)
    : null;

  const editingLocationDeleteBlockedReason = editingLocationForDelete
    ? getLocationDeleteBlockedReason(editingLocationForDelete)
    : null;

  const handleDelete = (location: Location) => {
    const latest = locationById.get(location.id) ?? location;
    const blockedReason = getLocationDeleteBlockedReason(latest);
    if (blockedReason) {
      messageApi.error(blockedReason);
      return;
    }

    AntModal.confirm({
      title: `エリアの削除`,
      content: `${latest.name} を削除してもよろしいですか？`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync({ id: latest.id });
          messageApi.success(`エリアを削除しました`);

          // 選択中のエリアが削除された場合はクリア
          if (selectedLocation?.id === latest.id) {
            setSelectedLocation(null);
          }

          // データを再取得
          refetch();
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : null;
          messageApi.error(errorMessage ?? `エリアの削除に失敗しました`);
        }
      },
    });
  };

  const deleteDisabledMessage = getDisabledMessage('manage');
  const actionMenuItems: MenuProps['items'] = [
    {
      key: 'copyHierarchy',
      label: canManage ? '階層をコピー' : (
        <Tooltip title={deleteDisabledMessage}>
          <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>階層をコピー</span>
        </Tooltip>
      ),
      icon: <CopyOutlined />,
      disabled: getMutationPendingState(copyHierarchyMutation) || !canManage,
    },
    {
      key: 'delete',
      label: canManage
        ? (
          selectedLocationDeleteBlockedReason
            ? (
              <Tooltip title={selectedLocationDeleteBlockedReason}>
                <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>削除</span>
              </Tooltip>
            )
            : '削除'
        )
        : (
          <Tooltip title={deleteDisabledMessage}>
            <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>削除</span>
          </Tooltip>
      ),
      danger: canManage,
      icon: <DeleteOutlined />,
      disabled: getMutationPendingState(deleteMutation) || !canManage || !!selectedLocationDeleteBlockedReason,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete' && selectedLocationForDelete) {
      handleDelete(selectedLocationForDelete);
    }
    if (key === 'copyHierarchy' && selectedLocation) {
      setCopyHierarchyName(`${selectedLocation.name}（コピー）`);
      setCopyHierarchyIncludeEquipments(true);
      setIsCopyHierarchyModalVisible(true);
    }
  };

  const pageMenuItems: MenuProps['items'] = [
    {
      key: 'branchesLegacy',
      label: '拠点管理',
      icon: <ApartmentOutlined />,
    },
  ];

  const handlePageMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'branchesLegacy') {
      navigate('/app/branches');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      const hasAddressInput = typeof values.address === 'string' && values.address.trim().length > 0;
      const hasGeocodedCoordinates = Boolean(values.latitude) && Boolean(values.longitude);
      const hasPinCoordinates = Boolean(values.pinLatitude) && Boolean(values.pinLongitude);
      if (shouldShowGeoAddressFields && hasAddressInput && !hasGeocodedCoordinates && !hasPinCoordinates) {
        messageApi.info('座標が未設定のため、地図上にピンは表示されません。保存後に地図から配置できます。');
      }

      const locationData = {
        name: values.name,
        description: values.description || null,
        parentId: values.parentId || null,
        categoryId: values.categoryId || null,
        mapMode: values.mapMode || null,
        displayOrder: typeof values.displayOrder === 'number' ? values.displayOrder : 0,
        ...(shouldShowGeoAddressFields ? {
          address: values.address || null,
          latitude: values.latitude || null,
          longitude: values.longitude || null,
          pinLatitude: values.pinLatitude || null,
          pinLongitude: values.pinLongitude || null,
        } : {}),
      };

      if (editingLocation) {
        // 既存のエリアを更新
        await updateMutation.mutateAsync({
          id: editingLocation.id,
          data: locationData,
        });
        messageApi.success(`エリアを更新しました`);
      } else {
        // 新規エリアを作成 - 現在選択中のbranchIdを設定
        if (!selectedBranchId) {
          messageApi.error('拠点を選択してください');
          return;
        }
        await createMutation.mutateAsync({
          ...locationData,
          ...(values.mapMode === 'image' && pendingCreateCoordinates ? pendingCreateCoordinates : {}),
          branchId: selectedBranchId,
        });
        await Promise.all([
          utils.location.getTree.invalidate(),
          utils.location.getList.invalidate(),
        ]);
        messageApi.success('エリアを作成しました');
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingLocation(null);
      setParentLocationName(null);
      setPendingCreateCoordinates(null);
      setGeocodeCandidates([]);

      // データを再取得
      refetch();
    } catch (error: unknown) {
      const hasFieldErrors = typeof error === 'object' && error !== null && 'errorFields' in error;
      if (hasFieldErrors) {
        return;
      }

      const trpcShapeMessage = typeof error === 'object' && error !== null
        ? (error as { shape?: { message?: unknown } }).shape?.message
        : null;
      const errorMessage =
        typeof trpcShapeMessage === 'string'
          ? trpcShapeMessage
          : (error instanceof Error ? error.message : 'エラーが発生しました');

      const trpcCode = typeof error === 'object' && error !== null
        ? (
          (error as { data?: { code?: unknown } }).data?.code
          ?? (error as { shape?: { data?: { code?: unknown } } }).shape?.data?.code
          ?? (error as { shape?: { code?: unknown } }).shape?.code
        )
        : null;

      if (trpcCode === 'CONFLICT') {
        form.setFields([{ name: 'name', errors: [errorMessage] }]);
      }

      messageApi.error(errorMessage);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingLocation(null);
    setParentLocationName(null);
    setPendingCreateCoordinates(null);
    setGeocodeCandidates([]);
  };

  const usedLocationCategoryIds = locationsTree
    ? new Set(
      getAllLocations(locationsTree)
        .map((location) => location.categoryId)
        .filter((categoryId): categoryId is string => typeof categoryId === 'string')
    )
    : new Set<string>();

  const selectedLocationId = selectedLocation?.id;
  const selectedLocationAddress = (selectedLocation?.address ?? '').trim();
  const mapSelectedLocationId = selectedLocationId ?? null;
  const assetsViewLocationId = selectedLocationId ?? mapScopeLocationId ?? null;
  const equipmentQueryEnabled = Boolean(assetsViewLocationId && companyId);
  const equipmentQueryInput = equipmentQueryEnabled
    ? { locationId: assetsViewLocationId as string, companyId: companyId as string }
    : undefined;

  const { data: equipmentData, refetch: refetchEquipment } = trpc.equipment.getByLocationId.useQuery(
    equipmentQueryInput,
    { enabled: equipmentQueryEnabled }
  );

  // 選択されたエリアの部品・消耗品を取得
  const { data: partData, refetch: refetchParts } = trpc.parts.getByLocationId.useQuery(
    equipmentQueryInput,
    { enabled: equipmentQueryEnabled }
  );

  const equipmentRows: EquipmentRow[] = useMemo(() => {
    if (!Array.isArray(equipmentData)) return [];
    return equipmentData
      .filter(isEquipmentApiResponse)
      .map((equipment, index) => {
        const id = equipment.id ?? index.toString();
        return {
          key: id,
          id,
          displayId: equipment.displayId ?? '-',
          name: equipment.name ?? '-',
          category: equipment.category ?? null,
          status: equipment.isActive ? '稼働中' : '停止中',
          childCount: equipment.childCount ?? 0,
        };
      });
  }, [equipmentData]);

  const partRows: PartRow[] = useMemo(() => {
    if (!Array.isArray(partData)) return [];
      return partData.map((part, index) => {
      const id = part.id ?? index.toString();
      return {
        key: id,
        id,
        displayId: part.displayId ?? '-',
        name: part.name ?? '-',
        partNumber: part.partNumber ?? undefined,
        category: normalizePartRowCategory(part.category),
        currentStockLevel: part.currentStockLevel ?? 0,
        optimalStockLevel: part.optimalStockLevel ?? 0,
      };
    });
  }, [partData]);

  const getTileIcon = useCallback((iconName?: string | null, size = 22) => {
    if (!iconName || !(iconName in Icons)) return null;
    const iconMap = Icons as unknown as Record<string, AntIconComponent>;
    const IconComponent = iconMap[iconName];
    if (!IconComponent) return null;
    return <IconComponent style={{ fontSize: size }} />;
  }, []);

  type ScopeAssetTile = {
    kind: 'equipment' | 'part';
    id: string;
    name: string;
    displayId: string;
    icon?: string | null;
    color?: string | null;
    badgeText?: string | null;
  };

  const scopeEquipmentTiles: ScopeAssetTile[] = useMemo(() => {
    return equipmentRows
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
      .map((row) => ({
        kind: 'equipment' as const,
        id: row.id,
        name: row.name,
        displayId: row.displayId,
        icon: row.category?.icon ?? null,
        color: row.category?.color ?? null,
        badgeText: row.status ?? null,
      }));
  }, [equipmentRows]);

  const scopePartTiles: ScopeAssetTile[] = useMemo(() => {
    return partRows
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'ja'))
      .map((row) => ({
        kind: 'part' as const,
        id: row.id,
        name: row.name,
        displayId: row.displayId,
        icon: row.category?.icon ?? null,
        color: row.category?.color ?? null,
        badgeText: typeof row.currentStockLevel === 'number' ? `在庫 ${row.currentStockLevel}` : null,
      }));
  }, [partRows]);

  return (
    <>
      {contextHolder}
      <div>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>データを読み込み中...</div>
          </div>
        ) : (
          <>
            {/* ヘッダー: タイトルとブランチセレクタ */}
            <LocationsHeader
              branches={branchOptions}
              selectedBranchId={selectedBranchId}
              onBranchChange={(value) => setSelectedBranchId(value)}
              onOpenCategories={() => navigate('/app/categories#location')}
              onOpenQrLabels={
                featureFlags.qrLabelPrinting
                  ? () => navigate('/app/qr-labels#location')
                  : undefined
              }
              pageMenuItems={pageMenuItems}
              onPageMenuClick={handlePageMenuClick}
            />

            {/* 空状態UI: エリアが0件でローディング中でない場合 - 全幅で表示 */}
            {!isLoading && treeData.length === 0 && (
              <LocationsEmptyStateCard
                token={token}
                onCreate={() => handleAdd()}
              />
            )}

            {/* 1段目: アドレスバー（マップ/プレビューの従属ヘッダ） */}
            <Row gutter={[12, 12]} style={{ marginBottom: 8 }}>
              <Col xs={0} lg={6} xl={5} />
              <Col xs={24} lg={18} xl={19}>
                {selectedBranchId && (
                  <div className="location-addressbar" style={{ marginBottom: 0 }}>
                    <Tooltip title="上へ">
                      <Button
                        size="small"
                        icon={<Icons.ArrowUpOutlined />}
                        onClick={handleScopeUp}
                        aria-label="上へ"
                        disabled={!mapScopeLocationId || (rootLocationId ? mapScopeLocationId === rootLocationId : false)}
                      />
                    </Tooltip>
                    <div className="location-addressbar__path">
                      <Breadcrumb
                        className="location-addressbar__breadcrumb"
                        items={[
                          {
                            title: (
                              <a
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (rootLocationId) {
                                    setMapScopeLocationId(rootLocationId);
                                  } else {
                                    setMapScopeLocationId(null);
                                  }
                                  setSelectedLocation(null);
                                }}
                              >
                                <HomeOutlined /> {selectedBranch?.name || '拠点'}
                              </a>
                            ),
                          },
                          ...scopeLocationChain.map((location) => ({
                            title: (
                              <a
                                onClick={(event) => {
                                  event.preventDefault();
                                  setMapScopeLocationId(location.id);
                                  setSelectedLocation(null);
                                }}
                              >
                                {location.name}
                              </a>
                            ),
                          })),
                        ]}
                      />
                    </div>
                  </div>
                )}
              </Col>
            </Row>

            {/* 2段目: 階層 +（マップ + プレビュー） */}
            <Row gutter={[12, 12]}>
              <Col xs={24} lg={6} xl={5}>
                <LocationsTreePanel
                  isLoading={isLoading}
                  selectedBranchId={selectedBranchId}
                  locationsTree={locationsTree}
                  rootLocationId={rootLocationId}
                  rootLocation={rootLocation}
                  treeData={treeData}
                  expandedKeys={expandedKeys}
                  onExpand={setExpandedKeys}
                  selectedTreeKeys={selectedTreeKeys}
                  onSelect={onSelect}
                  canManage={canManage}
                  isHierarchyMoveEnabled={isHierarchyMoveEnabled}
                  onHierarchyMoveEnabledChange={setIsHierarchyMoveEnabled}
                  onHierarchyDrop={handleHierarchyDrop}
                  isLocationDescendant={isLocationDescendant}
                  token={token}
                  onReload={refetch}
                />
              </Col>

              <Col xs={24} lg={18} xl={19}>
                <Row gutter={[12, 12]}>
                  <Col xs={24} lg={mapPanelSpan} xl={mapPanelSpan}>
                    <Card
                      title={
                        <Row wrap={false} align="middle" gutter={8} style={{ width: '100%' }}>
                          <Col flex="auto" style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                              {buildingContext.building ? (
                                <Tag color="geekblue" style={{ margin: 0 }}>
                                  建物
                                </Tag>
                              ) : currentScopeMapMode === 'geo' ? (
                                <Tag color="cyan" style={{ margin: 0 }}>
                                  地理地図
                                </Tag>
                              ) : null}
                              {buildingContext.building && activeFloorLabel ? (
                                <Tag color="purple" style={{ margin: 0 }}>
                                  {activeFloorLabel}
                                </Tag>
                              ) : null}
                              <span
                                style={{
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {mapScopeLocationId && rootLocationId && mapScopeLocationId === rootLocationId
                                  ? `${selectedBranch?.name || '拠点'}`
                                  : mapScopeLocationId
                                    ? `${scopeLocationChain[scopeLocationChain.length - 1]?.name || 'エリア'}`
                                    : `${selectedBranch?.name || '拠点'}`}
                              </span>
                            </div>
                          </Col>
                          <Col flex="none">
                            {scopeLocationForTitle && (
                              <Tooltip title="このエリアの設定">
                                <Button
                                  size="small"
                                  type="text"
                                  icon={<SettingOutlined />}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEdit(scopeLocationForTitle);
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Col>
                        </Row>
                      }
                      styles={{ body: { padding: 0 } }}
                      extra={
                        <Space wrap>
                          <Tooltip title="マップと詳細パネルの幅比を変更できます（ブラウザ拡大率では比率は変わりにくいため、ここで調整します）">
                            <Segmented
                              size="small"
                              value={mapPanelSpan}
                              onChange={(value) => setMapPanelSpan(value as number)}
                              options={[
                                { label: '狭', value: 14 },
                                { label: '標準', value: 16 },
                                { label: '広', value: 18 },
                              ]}
                            />
                          </Tooltip>
                          {mapScopeLocationId && isBuildingFloorScope && (
                            <Tooltip title="建物モードのフロアでは、表示対象を「全体（このマップ上）」または「階層含む（子孫含む）」で切り替えできます。">
                              <Segmented
                                size="small"
                                value={mapLocationsScopeMode}
                                onChange={(value) => setMapLocationsScopeMode(value as 'direct' | 'all')}
                                options={[
                                  { label: '全体', value: 'direct' },
                                  { label: '階層含む', value: 'all' },
                                ]}
                              />
                            </Tooltip>
                          )}
                          {mapScopeLocationId && currentScopeMapMode === 'geo' && (
                            <Tooltip title="地理地図では表示対象を「直下」または「配下すべて（子孫含む）」で切り替えできます。">
                              <Segmented
                                size="small"
                                value={mapLocationsScopeMode}
                                onChange={(value) => setMapLocationsScopeMode(value as 'direct' | 'all')}
                                options={[
                                  { label: '直下', value: 'direct' },
                                  { label: '配下すべて', value: 'all' },
                                ]}
                              />
                            </Tooltip>
                          )}
                          {mapScopeLocationId && currentScopeMapMode === 'geo' && canManage && (
                            <Tooltip title={isGeoClickPlaceMode ? '地図クリックで選択中エリアを移動します' : 'ONにすると地図クリックで選択中エリアを移動します'}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <Switch
                                  size="small"
                                  checked={isGeoClickPlaceMode}
                                  onChange={(checked) => setIsGeoClickPlaceMode(checked)}
                                />
                                <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                  クリックで配置
                                </span>
                              </span>
                            </Tooltip>
                          )}
                          {!mapScopeLocationId && currentScopeMapMode !== 'geo' ? (
                            <Tooltip title="この画面では拠点（branch）をデータ置き場として扱います。背景などは将来的にルートエリアへ集約予定です（現状は拠点の背景設定です）。">
                              <Button size="small" icon={<PictureOutlined />} onClick={() => setIsMapImageModalVisible(true)}>
                                背景（拠点・旧）
                              </Button>
                            </Tooltip>
                          ) : null}
                          {mapScopeLocationId && currentScopeMapMode !== 'geo' ? (
                            <Button size="small" icon={<PictureOutlined />} onClick={() => setIsMapImageModalVisible(true)}>
                              背景
                            </Button>
                          ) : null}
                          <Tooltip
                            title={
                              !canManage
                                ? '権限がないため変更できません'
                                : currentScopeMapMode === 'geo'
                                  ? '地理地図では位置固定は使用しません（画像マップのみ対象）'
                                  : !mapScopeLocationId
                                    ? '拠点トップは固定しません（将来ルートエリア化で設定予定）'
                                    : undefined
                            }
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <Switch
                                checked={isPositionLocked}
                                disabled={!canManage || getMutationPendingState(updateMutation) || !mapScopeLocationId || currentScopeMapMode === 'geo'}
                                onChange={(checked) => {
                                  handleTogglePositionLocked(checked);
                                }}
                              />
                              <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                                位置固定
                              </span>
                            </span>
                          </Tooltip>
                        </Space>
                      }
                    >
                      {locationsTree ? (
                        <>
                          <div data-testid="location-map-view">
                            {currentScopeMapMode === 'geo' ? (
                              <>
                                <Space wrap style={{ marginBottom: 8 }}>
                                {mapScopeLocationId ? (
                                  <Tag color="default">
                                    このエリア座標: {formattedGeoScopeCoordinates ?? '未設定'}
                                  </Tag>
                                ) : null}
                                {geoViewLocationStats.total === 0 ? (
                                  <Tag color="default">配下エリア: 0件</Tag>
                                ) : (
                                  <Tag color={geoViewLocationStats.withCoordinates > 0 ? 'blue' : 'default'}>
                                    配下ピン: {geoViewLocationStats.withCoordinates}/{geoViewLocationStats.total}
                                  </Tag>
                                )}
                                {geoViewLocationStats.virtualPinned > 0 ? (
                                  <Tag color="gold">
                                    仮配置: {geoViewLocationStats.virtualPinned}
                                  </Tag>
                                ) : null}
                                {geoViewLocationStats.total > 0 && geoViewLocationStats.withCoordinates === 0 ? (
                                  geoScopeCenter ? (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      配下エリアの座標が未設定のため、親座標の周りに仮配置で表示しています。
                                    </Text>
                                  ) : (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      配下エリアはありますが、座標が未設定のためピンが表示されません。
                                    </Text>
                                  )
                                ) : null}
                                </Space>
                                <div style={{ width: '100%', aspectRatio: '1 / 1', position: 'relative' }}>
                                <GeoLocationMapView
                                  height="100%"
                                  locations={geoDisplayLocations}
                                  fallbackCenter={geoScopeCenter}
                                  selectedLocationId={mapSelectedLocationId}
                                  onLocationClick={(location) => {
                                    const resolved = locationById.get(location.id) ?? null;
                                    setSelectedLocation(resolved);
                                  }}
                                  onLocationDoubleClick={(location) => {
                                    const resolved = locationById.get(location.id) ?? null;
                                    geoDebugLog('[LocationsPageWithAPI]', 'enter scope (geo pin dblclick)', {
                                      fromScopeLocationId: mapScopeLocationId,
                                      toScopeLocationId: location.id,
                                      resolvedMapMode: resolved?.mapMode ?? null,
                                    });
                                    if (mapScopeLocationId === location.id) {
                                      messageApi.info('すでにこのエリアのマップです。配下へ移動したい場合は、子エリアのピンをダブルクリックしてください。');
                                      return;
                                    }
                                    setMapScopeLocationId(location.id);
                                    setSelectedLocation(resolved);
                                  }}
                                  canEdit={canManage}
                                  onLocationMove={(location, lat, lon) => {
                                    void handleMoveGeoLocationOnMap(location.id, lat, lon);
                                  }}
                                  onMapBackgroundClick={
                                    !isGeoClickPlaceMode
                                      ? () => {
                                        setSelectedLocation(null);
                                      }
                                      : undefined
                                  }
                                  onMapClick={
                                    canManage && isGeoClickPlaceMode
                                      ? (lat, lon) => {
                                        if (!selectedLocation) {
                                          messageApi.info('移動したいエリアを選択してください');
                                          return;
                                        }
                                        if (!selectableLocationIdSet.has(selectedLocation.id)) {
                                          messageApi.info('表示範囲外のエリアは移動できません');
                                          return;
                                        }
                                        void handleMoveGeoLocationOnMap(selectedLocation.id, lat, lon);
                                      }
                                      : undefined
                                  }
                                />
                                </div>
                              </>
                            ) : (
                              <FactoryLayoutViewResponsive
                              locations={scopedMapLocations.map(l => ({
                                id: l.id,
                                name: l.name,
                                xCoordinate: l.mapX ?? undefined,
                                yCoordinate: l.mapY ?? undefined,
                                equipmentCount: l.equipmentCount ?? 0,
                                objectCount: objectCountById.get(l.id) ?? 0,
                                mapMode: l.mapMode ?? undefined,
                                level: l.level,
                                description: l.description ?? undefined,
                                icon: l.icon ?? undefined,
                                categoryId: l.categoryId ?? undefined,
                                thumbnailFileId: l.thumbnailFileId ?? undefined,
                              }))}
                              selectedLocationId={mapSelectedLocationId}
                              onLocationClick={(location) => {
                                const resolved = locationById.get(location.id) ?? null;
                                setSelectedLocation(resolved);
                              }}
                              onLocationDoubleClick={(location) => {
                                setMapScopeLocationId(location.id);
                                const resolved = locationById.get(location.id) ?? null;
                                setSelectedLocation(resolved);
                              }}
                              onBackgroundClick={() => {
                                setSelectedLocation(null);
                              }}
                              onRequestMapImageSettings={
                                canManage ? () => setIsMapImageModalVisible(true) : undefined
                              }
                              backgroundImageUrl={currentMapBackgroundImageUrl}
                              backgroundFillColor={scopeLocation?.mapBackgroundColor ?? null}
                              mapMode={currentScopeMapMode}
                              showTitle={false}
                              categories={locationCategories}
                              thumbnailUrlByFileId={thumbnailUrlByFileId}
                              isBulkPositionEditMode={isBulkPositionEditMode}
                              onLocationMove={handleMoveLocationOnMap}
                              onLocationsMove={handleMoveLocationsOnMap}
                                onRequestCreateAt={canManage ? handleQuickCreateAt : undefined}
                              />
                            )}
                          </div>
                          <div
                            style={{
                              margin: '0 12px 12px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 8,
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <LocationCategoryLegend
                                categories={locationCategories}
                                usedCategoryIds={usedLocationCategoryIds}
                              />
                            </div>
                            <Tooltip title={scopeLocationForTitle ? 'このマップ（エリア）の直下に新規エリアを作成します' : undefined}>
                              <div>
                                <PermissionButton
                                  type="primary"
                                  size="small"
                                  icon={<PlusOutlined />}
                                  onClick={() => {
                                    if (!scopeLocationForTitle) return;
                                    handleAdd(scopeLocationForTitle);
                                  }}
                                  disabled={!scopeLocationForTitle}
                                  requiredPermission="manage"
                                >
                                  新規エリア
                                </PermissionButton>
                              </div>
                            </Tooltip>
                          </div>
                        </>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                          <Text type="secondary">エリアを選択してください</Text>
                        </div>
                      )}
                    </Card>
                  </Col>

                  <Col xs={24} lg={24 - mapPanelSpan} xl={24 - mapPanelSpan}>
                    {buildingContext.building && (
                      <div style={{ marginBottom: 12 }}>
                        <BuildingFloorNavigator
                          buildingName={buildingContext.building.name}
                          floors={buildingFloorsForNavigator}
                          activeFloorId={buildingContext.activeFloorId}
                          onSelectFloor={(floorId) => {
                            setMapScopeLocationId(floorId);
                            const resolved = locationById.get(floorId) ?? null;
                            setSelectedLocation(resolved);
                          }}
                        />
                      </div>
                    )}

                    {selectedLocation ? (
                      <>
                        <Card
                          title={(
                            <Row wrap={false} align="middle" gutter={12} style={{ width: '100%' }}>
                              <Col flex="auto" style={{ minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                  <Text type="secondary">エリア</Text>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {selectedLocation.name}
                                  </span>
                                </div>
                              </Col>
                              <Col flex="170px">
                                <Tooltip title={!canManage ? getDisabledMessage('manage') : 'カテゴリ（必須）を変更'}>
                                  <span>
                                    <Select
                                      size="small"
                                      style={{ width: '100%' }}
                                      placeholder="カテゴリ（必須）"
                                      value={selectedLocation.categoryId ?? undefined}
                                      options={locationCategoryOptions}
                                      onChange={handleChangeSelectedLocationCategory}
                                      disabled={!canManage || getMutationPendingState(updateMutation)}
                                      status={selectedLocation.categoryId ? undefined : 'error'}
                                    />
                                  </span>
                                </Tooltip>
                              </Col>
                            </Row>
                          )}
                          extra={
                            <Tooltip title={!canManage ? getDisabledMessage('manage') : '編集'}>
                              <span>
                                <PermissionButton
                                  type="text"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEdit(selectedLocation)}
                                  requiredPermission="manage"
                                  aria-label="編集"
                                  children={null}
                                />
                              </span>
                            </Tooltip>
                          }
                          style={{ marginBottom: 16 }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
                            <PermissionButton
                              size="small"
                              icon={<PictureOutlined />}
                              onClick={() => {
                                setThumbnailTargetLocationId(selectedLocation.id);
                                setIsThumbnailModalVisible(true);
                              }}
                              requiredPermission="manage"
                            >
                              サムネイル画像
                            </PermissionButton>
                            <Dropdown
                              menu={{ items: actionMenuItems, onClick: handleMenuClick }}
                              trigger={['click']}
                              placement="bottomRight"
                              disabled={getMutationPendingState(deleteMutation)}
                            >
                              <Button
                                size="small"
                                icon={<MoreOutlined />}
                                aria-label="その他の操作"
                                loading={getMutationPendingState(deleteMutation)}
                              />
                            </Dropdown>
                          </div>
                          <List
                            size="small"
                            split={false}
                            className="location-detail-list"
                            dataSource={[
                              {
                                label: '詳細',
                                content: (
                                  <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    {selectedLocation.description ? (
                                      <MarkdownDisplay content={selectedLocation.description} />
                                    ) : selectedLocationAddress ? null : (
                                      <Text type="secondary">-</Text>
                                    )}
                                    {selectedLocationAddress ? (
                                      <Text type="secondary">
                                        <EnvironmentOutlined style={{ marginRight: 6 }} />
                                        {selectedLocationAddress}
                                      </Text>
                                    ) : null}
                                  </Space>
                                ),
                              },
                            ]}
                            renderItem={(item) => (
                              <List.Item>
                                <div className="location-detail-list__row">
                                  <Text type="secondary" className="location-detail-list__label">
                                    {item.label}
                                  </Text>
                                  <div className="location-detail-list__value">{item.content}</div>
                                </div>
                              </List.Item>
                            )}
                          />

                          {/* 詳細表示トグル */}
                          <div
                            style={{
                              padding: '8px 0',
                              cursor: 'pointer',
                              color: '#999',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 12,
                            }}
                            onClick={() => setShowDetailInfo(!showDetailInfo)}
                          >
                            {showDetailInfo ? <Icons.UpOutlined /> : <Icons.DownOutlined />}
                            {showDetailInfo ? '詳細を隠す' : '詳細を表示'}
                          </div>

                          {/* 折りたたみ可能な詳細情報 */}
                          {showDetailInfo && (
                            <List
                              size="small"
                              bordered
                              className="location-detail-list"
                              dataSource={[
                                {
                                  label: 'レベル',
                                  content: (
                                    <Space size={8} wrap>
                                      <span>{selectedLocation.level + 1}</span>
                                      <Tag color={getLocationMapModeTagColor(selectedLocation.mapMode)}>
                                        {getLocationMapModeLabel(selectedLocation.mapMode)}
                                      </Tag>
                                    </Space>
                                  ),
                                },
                                {
                                  label: 'マップ座標',
                                  content: typeof selectedLocation.mapX === 'number' && typeof selectedLocation.mapY === 'number' ? (
                                    <Tag color="green">X={selectedLocation.mapX}, Y={selectedLocation.mapY}</Tag>
                                  ) : (
                                    <Tag color="default">未設定</Tag>
                                  ),
                                },
                                {
                                  label: '作成者',
                                  content: selectedLocation.createdByUser ? (
                                    <UserDisplay user={selectedLocation.createdByUser} />
                                  ) : (
                                    <Typography.Text type="secondary">不明</Typography.Text>
                                  ),
                                },
                                {
                                  label: '最終編集者',
                                  content: selectedLocation.updatedByUser ? (
                                    <UserDisplay user={selectedLocation.updatedByUser} />
                                  ) : (
                                    <Typography.Text type="secondary">未編集</Typography.Text>
                                  ),
                                },
                                {
                                  label: '作成日時',
                                  content: selectedLocation.createdAt
                                    ? dayjs(selectedLocation.createdAt).format('YYYY/MM/DD HH:mm')
                                    : '-',
                                },
                                {
                                  label: '最終更新日時',
                                  content: selectedLocation.updatedAt
                                    ? dayjs(selectedLocation.updatedAt).format('YYYY/MM/DD HH:mm')
                                    : '-',
                                },
                              ]}
                              renderItem={(item) => (
                                <List.Item>
                                  <div className="location-detail-list__row">
                                    <Text type="secondary" className="location-detail-list__label">
                                      {item.label}
                                    </Text>
                                    <div className="location-detail-list__value">{item.content}</div>
                                  </div>
                                </List.Item>
                              )}
                            />
                          )}
                          <Divider style={{ margin: '12px 0' }} />

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 10,
                            }}
                          >
                            <Space size={8}>
                              <Icons.ToolOutlined style={{ color: token.colorPrimary }} />
                              <Text strong style={{ fontSize: 14 }}>設備</Text>
                            </Space>
                            <Space size={8}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {scopeEquipmentTiles.length}件
                              </Text>
                              <Tooltip title={isEquipmentAtCapacity ? '設備登録数が上限に達しています' : undefined}>
                                <PermissionButton
                                  size="small"
                                  icon={<PlusOutlined />}
                                  onClick={() => {
                                    if (isEquipmentAtCapacity) {
                                      messageApi.warning('設備登録数が上限に達しています。追加オプションを追加してください。');
                                      return;
                                    }
                                    setEquipmentCreateCategoryId(null);
                                    setIsEquipmentModalVisible(true);
                                  }}
                                  disabled={!assetTargetLocationId || isEquipmentAtCapacity}
                                  requiredPermission="manage"
                                >
                                  追加
                                </PermissionButton>
                              </Tooltip>
                            </Space>
                          </div>
                          {scopeEquipmentTiles.length === 0 ? (
                            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                              設備はまだありません
                            </Text>
                          ) : (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, 72px)',
                                justifyContent: 'start',
                                gap: 4,
                                marginBottom: 12,
                              }}
                            >
                              {scopeEquipmentTiles.map((tile) => (
                                <div
                                  key={`equipment:${tile.id}`}
                                  style={{
                                    padding: 0,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    textAlign: 'center',
                                  }}
                                  onClick={() => setViewingEquipmentId(tile.id)}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                                    <div
                                      style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 16,
                                        background: token.colorBgContainer,
                                        border: `1px solid ${token.colorBorderSecondary}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: tile.color ?? token.colorPrimary,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                                      }}
                                    >
                                      {getTileIcon(tile.icon, 24) ?? <Icons.ToolOutlined style={{ fontSize: 24 }} />}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 400,
                                      lineHeight: 1.2,
                                      maxWidth: 72,
                                      margin: '0 auto',
                                      whiteSpace: 'normal',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }}
                                    title={tile.name}
                                  >
                                    {tile.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <Divider style={{ margin: '14px 0', borderColor: token.colorBorderSecondary }} />

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 10,
                            }}
                          >
                            <Space size={8}>
                              <Icons.InboxOutlined style={{ color: token.colorSuccess }} />
                              <Text strong style={{ fontSize: 14 }}>部品・消耗品</Text>
                            </Space>
                            <Space size={8}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {scopePartTiles.length}件
                              </Text>
                              <PermissionButton
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                  setPartCreateCategoryId(null);
                                  setIsPartModalVisible(true);
                                }}
                                disabled={!assetTargetLocationId}
                                requiredPermission="manage"
                              >
                                追加
                              </PermissionButton>
                            </Space>
                          </div>
                          {scopePartTiles.length === 0 ? (
                            <Text type="secondary" style={{ display: 'block' }}>
                              部品・消耗品はまだありません
                            </Text>
                          ) : (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, 72px)',
                                justifyContent: 'start',
                                gap: 4,
                              }}
                            >
                              {scopePartTiles.map((tile) => (
                                <div
                                  key={`part:${tile.id}`}
                                  style={{
                                    padding: 0,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    textAlign: 'center',
                                  }}
                                  onClick={() => setViewingPartId(tile.id)}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                                    <div
                                      style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 16,
                                        background: token.colorBgContainer,
                                        border: `1px solid ${token.colorBorderSecondary}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: tile.color ?? token.colorSuccess,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                                      }}
                                    >
                                      {getTileIcon(tile.icon, 24) ?? <Icons.InboxOutlined style={{ fontSize: 24 }} />}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 400,
                                      lineHeight: 1.2,
                                      maxWidth: 72,
                                      margin: '0 auto',
                                      whiteSpace: 'normal',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }}
                                    title={tile.name}
                                  >
                                    {tile.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <Divider style={{ margin: '14px 0', borderColor: token.colorBorderSecondary }} />
                          <LocationDocumentsSection
                            entityId={selectedLocation.id}
                            allowOfficial={selectedLocationIsBuilding}
                            onOpenDocument={(documentId) => setViewingDocumentId(documentId)}
                          />
                        </Card>
                      </>
                    ) : (
                      <>
                        <Card
                          title={(
                            <Space size={8}>
                              <span>設備・部品</span>
                              {scopeLocation?.name ? <Text type="secondary">（{scopeLocation.name}）</Text> : null}
                            </Space>
                          )}
                          style={{ marginTop: 0 }}
                          extra={
                            <Space direction="vertical" size={8} style={{ alignItems: 'flex-end' }}>
                              <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                  if (!equipmentQueryEnabled) {
                                    return;
                                  }
                                  void refetchEquipment();
                                  void refetchParts();
                                }}
                                disabled={!equipmentQueryEnabled}
                              >
                                更新
                              </Button>
                            </Space>
                          }
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 10,
                            }}
                          >
                            <Space size={8}>
                              <Icons.ToolOutlined style={{ color: token.colorPrimary }} />
                              <Text strong style={{ fontSize: 14 }}>設備</Text>
                            </Space>
                            <Space size={8}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {scopeEquipmentTiles.length}件
                              </Text>
                              <Tooltip title={isEquipmentAtCapacity ? '設備登録数が上限に達しています' : undefined}>
                                <PermissionButton
                                  size="small"
                                  icon={<PlusOutlined />}
                                  onClick={() => {
                                    if (isEquipmentAtCapacity) {
                                      messageApi.warning('設備登録数が上限に達しています。追加オプションを追加してください。');
                                      return;
                                    }
                                    setEquipmentCreateCategoryId(null);
                                    setIsEquipmentModalVisible(true);
                                  }}
                                  disabled={!assetTargetLocationId || isEquipmentAtCapacity}
                                  requiredPermission="manage"
                                >
                                  追加
                                </PermissionButton>
                              </Tooltip>
                            </Space>
                          </div>
                          {scopeEquipmentTiles.length === 0 ? (
                            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                              設備はまだありません
                            </Text>
                          ) : (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, 72px)',
                                justifyContent: 'start',
                                gap: 4,
                                marginBottom: 12,
                              }}
                            >
                              {scopeEquipmentTiles.map((tile) => (
                                <div
                                  key={`equipment:${tile.id}`}
                                  style={{
                                    padding: 0,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    textAlign: 'center',
                                  }}
                                  onClick={() => setViewingEquipmentId(tile.id)}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                                    <div
                                      style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 16,
                                        background: token.colorBgContainer,
                                        border: `1px solid ${token.colorBorderSecondary}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: tile.color ?? token.colorPrimary,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                                      }}
                                    >
                                      {getTileIcon(tile.icon, 24) ?? <Icons.ToolOutlined style={{ fontSize: 24 }} />}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 400,
                                      lineHeight: 1.2,
                                      maxWidth: 72,
                                      margin: '0 auto',
                                      whiteSpace: 'normal',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }}
                                    title={tile.name}
                                  >
                                    {tile.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <Divider style={{ margin: '14px 0', borderColor: token.colorBorderSecondary }} />

                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: 10,
                            }}
                          >
                            <Space size={8}>
                              <Icons.InboxOutlined style={{ color: token.colorSuccess }} />
                              <Text strong style={{ fontSize: 14 }}>部品・消耗品</Text>
                            </Space>
                            <Space size={8}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {scopePartTiles.length}件
                              </Text>
                              <PermissionButton
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                  setPartCreateCategoryId(null);
                                  setIsPartModalVisible(true);
                                }}
                                disabled={!assetTargetLocationId}
                                requiredPermission="manage"
                              >
                                追加
                              </PermissionButton>
                            </Space>
                          </div>
                          {scopePartTiles.length === 0 ? (
                            <Text type="secondary" style={{ display: 'block' }}>
                              部品・消耗品はまだありません
                            </Text>
                          ) : (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, 72px)',
                                justifyContent: 'start',
                                gap: 4,
                              }}
                            >
                              {scopePartTiles.map((tile) => (
                                <div
                                  key={`part:${tile.id}`}
                                  style={{
                                    padding: 0,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    textAlign: 'center',
                                  }}
                                  onClick={() => setViewingPartId(tile.id)}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
                                    <div
                                      style={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 16,
                                        background: token.colorBgContainer,
                                        border: `1px solid ${token.colorBorderSecondary}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: tile.color ?? token.colorSuccess,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                                      }}
                                    >
                                      {getTileIcon(tile.icon, 24) ?? <Icons.InboxOutlined style={{ fontSize: 24 }} />}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 400,
                                      lineHeight: 1.2,
                                      maxWidth: 72,
                                      margin: '0 auto',
                                      whiteSpace: 'normal',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                    }}
                                    title={tile.name}
                                  >
                                    {tile.name}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          <Divider style={{ margin: '14px 0', borderColor: token.colorBorderSecondary }} />
                          <LocationDocumentsSection
                            entityId={assetTargetLocationId}
                            allowOfficial={assetTargetLocationIsBuilding}
                            onOpenDocument={(documentId) => setViewingDocumentId(documentId)}
                          />
                        </Card>
                      </>
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        )}

        <DevModal
          title="エリア設定"
          open={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
          okText="OK"
          cancelText="キャンセル"
          confirmLoading={getMutationPendingState(createMutation) || getMutationPendingState(updateMutation)}
          footer={(originNode) => {
            if (!editingLocationForDelete) {
              return originNode;
            }

            return (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tooltip title={canManage ? editingLocationDeleteBlockedReason ?? undefined : undefined}>
                  <span>
                    <PermissionButton
                      danger
                      type="primary"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(editingLocationForDelete)}
                      disabled={getMutationPendingState(deleteMutation) || !!editingLocationDeleteBlockedReason}
                      loading={getMutationPendingState(deleteMutation)}
                      requiredPermission="manage"
                    >
                      削除
                    </PermissionButton>
                  </span>
                </Tooltip>
                <div>{originNode}</div>
              </div>
            );
          }}
          forceRender
        >
          <Form
            form={form}
            layout="vertical"
          >
            {parentLocationName && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', marginBottom: 8 }}>親エリア</label>
                <Input value={parentLocationName} disabled />
              </div>
            )}
            <Form.Item name="parentId" hidden>
              <Input />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="エリア名"
                  rules={[{ required: true, message: 'エリア名を入力してください' }]}
                >
                  <Input placeholder="例: 第1製造棟" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="categoryId"
                  label="カテゴリ"
                  rules={[{ required: true, message: 'カテゴリを選択してください' }]}
                >
                  <Select
                    placeholder="カテゴリを選択（必須）"
                    options={locationCategoryOptions}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label="詳細"
              getValueFromEvent={(markdown: string) => markdown}
              getValueProps={(value) => ({ value: value || '' })}
            >
              <RichTextEditor
                placeholder="このエリアの詳細を入力..."
                minHeight={100}
                showPreviewToggle={false}
              />
            </Form.Item>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="mapMode"
                  label="マップモード"
                  initialValue="image"
                  tooltip="「画像マップ」は画像マップに配置、「建物」はフロア切替表示、「フォルダ」はマップなし一覧表示向けです。"
                >
                  <Select
                    options={[
                      { label: '画像マップ', value: 'image' },
                      { label: '建物', value: 'building' },
                      { label: 'フォルダ', value: 'folder' },
                      { label: '地理地図', value: 'geo' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="displayOrder"
                  label="表示順（フロア順）"
                  initialValue={0}
                  tooltip="小さいほど上（先）に表示されます。建物配下のフロア順を揃える用途に使えます。"
                >
                  <NumericInput min={0} />
                </Form.Item>
              </Col>
            </Row>
            {shouldShowGeoAddressFields && (
              <>
                <Form.Item
                  label="住所（地理地図）"
                  tooltip="住所検索して緯度・経度を設定します（地図上のピン位置に反映されます）。"
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="address" noStyle>
                      <Input
                        placeholder="例: 東京都千代田区丸の内1-9-1"
                        allowClear
                        onPressEnter={() => {
                          void handleGeocodeSearch();
                        }}
                      />
                    </Form.Item>
                    <Button
                      onClick={() => {
                        void handleGeocodeSearch();
                      }}
                      loading={geocodeAddressQuery.isFetching}
                    >
                      住所検索
                    </Button>
                  </Space.Compact>
                </Form.Item>
                <Form.Item name="latitude" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="longitude" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="pinLatitude" hidden>
                  <Input />
                </Form.Item>
                <Form.Item name="pinLongitude" hidden>
                  <Input />
                </Form.Item>
                {geocodeCandidates.length > 1 && (
                  <Form.Item label="住所候補">
                    <Select
                      showSearch
                      placeholder="候補を選択"
                      optionFilterProp="label"
                      options={geocodeCandidates.map((candidate, index) => ({
                        label: candidate.displayName,
                        value: String(index),
                      }))}
                      onChange={(value) => {
                        const index = Number(value);
                        const candidate = geocodeCandidates[index];
                        if (candidate) {
                          applyGeocodeCandidate(candidate);
                        }
                      }}
                    />
                  </Form.Item>
                )}
              </>
            )}
          </Form>
        </DevModal>

        {/* マップ画像変更モーダル */}
        <DevModal
          title={`マップ画像設定 - ${mapScopeLocationId
            ? (scopeLocationChain[scopeLocationChain.length - 1]?.name || 'エリア')
            : (selectedBranch?.name || '')}`}
          open={isMapImageModalVisible}
          onCancel={() => {
            setIsMapImageModalVisible(false);
            // モーダルを閉じたときに画像を再取得
            if (mapScopeLocationId) {
              refetchScopedLocationMapImage();
            } else {
              refetchLocationMapImage();
            }
          }}
          footer={null}
          width={800}
          destroyOnHidden
        >
          {mapScopeLocationId ? (
            <LocationMapImageSettings locationId={mapScopeLocationId} canEdit={true} />
          ) : (
            selectedBranchId && (
              <BranchLocationMapSettings
                branchId={selectedBranchId}
                canEdit={true}
              />
            )
          )}
        </DevModal>

        {/* サムネイル画像変更モーダル */}
        <DevModal
          title={`サムネイル画像設定 - ${thumbnailTargetLocationId
            ? (locationById.get(thumbnailTargetLocationId)?.name || 'エリア')
            : ''}`}
          open={isThumbnailModalVisible}
          onCancel={() => {
            setIsThumbnailModalVisible(false);
            setThumbnailTargetLocationId(null);
          }}
          footer={null}
          width={720}
          destroyOnHidden
        >
          {thumbnailTargetLocationId ? (
            <LocationThumbnailSettings locationId={thumbnailTargetLocationId} canEdit={true} />
          ) : null}
        </DevModal>

        <AntModal
          title="階層をコピー"
          open={isCopyHierarchyModalVisible}
          okText="コピー実行"
          cancelText="キャンセル"
          confirmLoading={getMutationPendingState(copyHierarchyMutation)}
          onCancel={() => setIsCopyHierarchyModalVisible(false)}
          onOk={async () => {
            if (!selectedLocation) {
              setIsCopyHierarchyModalVisible(false);
              return;
            }
            try {
              const result = await copyHierarchyMutation.mutateAsync({
                sourceId: selectedLocation.id,
                newName: copyHierarchyName,
                includeEquipments: copyHierarchyIncludeEquipments,
              });
              messageApi.success('階層をコピーしました');
              setIsCopyHierarchyModalVisible(false);

              const refreshed = await refetch();
              const all = getAllLocations(normalizeLocationTree(refreshed.data));
              const copied = all.find((l) => l.id === result.rootLocationId) ?? null;
              if (copied) {
                setSelectedLocation(copied as unknown as Location);
              }
            } catch (error: unknown) {
              const errorMessage = error instanceof Error ? error.message : null;
              if (
                copyHierarchyIncludeEquipments &&
                typeof errorMessage === 'string' &&
                (errorMessage.includes('上限') || errorMessage.includes('超え'))
              ) {
                AntModal.warning({
                  title: '設備登録上限を超えます',
                  content: '設備をコピーすると登録上限を超えるため実行できません。必要であれば「設備もコピーする」のチェックを外して再実行してください。',
                  okText: 'OK',
                });
              } else {
                messageApi.error(errorMessage ?? '階層コピーに失敗しました');
              }
            }
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>コピー先の名称</div>
              <Input
                value={copyHierarchyName}
                onChange={(event) => setCopyHierarchyName(event.target.value)}
                placeholder="例: 2F"
              />
            </div>
            <Checkbox
              checked={copyHierarchyIncludeEquipments}
              onChange={(event) => setCopyHierarchyIncludeEquipments(event.target.checked)}
            >
              設備もコピーする（新規設備として作成）
            </Checkbox>
            <div style={{ color: '#666', fontSize: 12 }}>
              コピーは「同じ親エリア配下」に作成されます。必要に応じて後から移動/名称変更してください。
            </div>
          </div>
        </AntModal>

        {/* 設備新規作成モーダル */}
        <EquipmentFormModal
          open={isEquipmentModalVisible}
          title="設備新規登録"
          initialValues={
            assetTargetLocationId || equipmentCreateCategoryId
              ? {
                locationIds: assetTargetLocationId,
                categoryId: equipmentCreateCategoryId ?? undefined,
              }
              : null
          }
          onCancel={() => {
            setIsEquipmentModalVisible(false);
            setEquipmentCreateCategoryId(null);
          }}
          onSubmit={async ({ values, locationIds }) => {
            await equipmentCreateMutation.mutateAsync({
              ...values,
              name: typeof values.name === 'string' ? values.name : '',
              locationIds,
            });
          }}
          confirmLoading={getMutationPendingState(equipmentCreateMutation)}
        />

        {/* 部品新規作成モーダル */}
        <PartFormModal
          open={isPartModalVisible}
          part={null}
          initialLocationId={assetTargetLocationId}
          initialCategoryId={partCreateCategoryId}
          onClose={() => {
            setIsPartModalVisible(false);
            setPartCreateCategoryId(null);
          }}
          onSuccess={() => {
            setIsPartModalVisible(false);
            setPartCreateCategoryId(null);
            refetch();
            if (mapScopeLocationId && companyId) {
              refetchParts();
              refetchEquipment();
            }
          }}
        />

        <EquipmentDetailModal
          open={!!viewingEquipmentId}
          equipmentId={viewingEquipmentId}
          onClose={() => setViewingEquipmentId(null)}
        />

        <PartDetailModal
          open={!!viewingPartId}
          partId={viewingPartId}
          onClose={() => setViewingPartId(null)}
        />

        <DocumentDetailModal
          open={!!viewingDocumentId}
          documentId={viewingDocumentId}
          onClose={() => setViewingDocumentId(null)}
        />
      </div>
    </>
  );
}
